import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Layout} from '@/components/Layout';
import {PermissionGate} from '@/components/PermissionGate';
import {VehicleImagePicker} from '@/components/VehicleImagePicker';
import {
    Car, User, Phone, Mail, QrCode, AlertTriangle, CheckCircle2, XCircle,
    Search, Plus, Edit, Eye, Package, Ban, Clock, MapPin, UserCog, History,
    FileText, Calendar, Shield, MessageSquare, PhoneCall, Timer, TrendingUp,
    BarChart3, ArrowLeftRight, Users, Bell, Trash2, ChevronRight, Link2, Unlink,
    LayoutGrid, List, Bike, Truck, Cog, Zap, Box, Image as ImageIcon, X,
    Check, ChevronsUpDown
} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter} from '@/components/ui/dialog';
import {Label} from '@/components/ui/label';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {Checkbox} from '@/components/ui/checkbox';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover';
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from '@/components/ui/command';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs';
import {toast} from 'sonner';
import {PERMISSIONS} from '@/lib/permissions';
import {usePermissions} from '@/hooks/usePermissions';
import {api, departmentEndpoints, driverContactRuleEndpoints, getApiErrorMessage, objectTypeEndpoints, occurrenceEndpoints, resourceContactRuleGroupEndpoints, resourceEndpoints, resourceStatusEndpoints, tagEndpoints, userEndpoints, userResourceAssignmentEndpoints} from '@/lib/api';
import {
    normalizeAvailableTags,
} from '@/data/api';


const OBJECT_TYPE_ICONS = {Car, Bike, Truck, Cog, Zap, Box};

const statusConfig = {
    maintenance: {color: 'bg-amber-100 text-amber-700', label: 'Manutenção'},
    active: {color: 'bg-emerald-100 text-emerald-700', label: 'Ativo'},
    inactive: {color: 'bg-slate-100 text-slate-600', label: 'Inativo'},
    extinct: {color: 'bg-red-100 text-red-700', label: 'Extinto'},
};

const getStatusConfig = (status) => statusConfig[status] || statusConfig.inactive;
const resourceStatusOptions = Object.entries(statusConfig).map(([value, config]) => ({
    value,
    label: config.label,
}));
const normalizeStatusText = (value) =>
    String(value ?? '')
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
const resourceStatusAliases = {
    maintenance: 'maintenance',
    manutencao: 'maintenance',
    active: 'active',
    ativo: 'active',
    inactive: 'inactive',
    inativo: 'inactive',
    extinct: 'extinct',
    extinto: 'extinct',
};
const normalizeResourceStatusKey = (value) => {
    const normalized = normalizeStatusText(value);
    return resourceStatusAliases[normalized] || (statusConfig[normalized] ? normalized : '');
};
const incidentStatusConfig = {
    pending: {color: 'bg-red-100 text-red-700', label: 'Pendente'},
    in_progress: {color: 'bg-amber-100 text-amber-700', label: 'Em Curso'},
    resolved: {color: 'bg-emerald-100 text-emerald-700', label: 'Resolvido'},
};
const tagReasonConfig = {
    lost: {color: 'bg-red-100 text-red-700', label: 'Perdida', icon: AlertTriangle},
    damaged: {color: 'bg-amber-100 text-amber-700', label: 'Danificada', icon: XCircle},
    other: {color: 'bg-slate-100 text-slate-600', label: 'Outro', icon: Ban},
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const safeJson = (value, fallback) => {
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
};

const arrayFrom = (value) => {
    const parsed = safeJson(value, value);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return Object.values(parsed);
    return [];
};

const toApiId = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isInteger(number) ? number : value;
};

const getPathValue = (source = {}, path = '') =>
    String(path || '').split('.').filter(Boolean).reduce((acc, key) => acc?.[key], source);

const setPathValue = (source = {}, path = '', value) => {
    const keys = String(path || '').split('.').filter(Boolean);
    if (!keys.length) return source;
    const next = {...source};
    let cursor = next;
    keys.forEach((key, index) => {
        if (index === keys.length - 1) {
            cursor[key] = value;
            return;
        }
        cursor[key] = cursor[key] && typeof cursor[key] === 'object' && !Array.isArray(cursor[key])
            ? {...cursor[key]}
            : {};
        cursor = cursor[key];
    });
    return next;
};

const normalizeFieldOptions = (options) =>
    arrayFrom(options).map((option, index) => {
        if (option && typeof option === 'object') {
            const value = option.value ?? option.option_value ?? option.key ?? option.code ?? option.name ?? option.label ?? option.id ?? index;
            return {
                value: String(value),
                label: option.label ?? option.name ?? option.title ?? String(value),
            };
        }
        return {value: String(option), label: String(option)};
    });

const normalizeInputType = (type = 'text') => {
    const normalized = String(type || 'text').toLowerCase();
    if (['select', 'dropdown', 'radio'].includes(normalized)) return 'select';
    if (['number', 'numeric', 'integer', 'decimal', 'float'].includes(normalized)) return 'number';
    if (['textarea', 'long_text', 'text_area'].includes(normalized)) return 'textarea';
    if (['coordinates', 'coordinate', 'location', 'coords'].includes(normalized)) return 'coords';
    if (['date', 'time', 'datetime-local', 'email', 'tel', 'url'].includes(normalized)) return normalized;
    return 'text';
};

const normalizeObjectTypeField = (field = {}, index = 0) => ({
    ...field,
    id: field.id ?? field.attribute_key ?? field.key ?? field.name ?? `field_${index + 1}`,
    attributeKey: field.attribute_key ?? field.key ?? field.name ?? `field_${index + 1}`,
    valuePath: field.value_path ?? (field.attribute_key ? `attributes.${field.attribute_key}` : field.key ? `attributes.${field.key}` : `attributes.field_${index + 1}`),
    label: field.label ?? field.name ?? field.title ?? field.attribute_key ?? `Campo ${index + 1}`,
    type: normalizeInputType(field.input_type ?? field.field_type ?? field.type),
    required: field.required === true || field.required === 1 || field.required === '1' || field.is_required === true || field.validation?.required === true,
    validation: field.validation ?? {},
    options: normalizeFieldOptions(field.options),
});

const normalizeObjectType = (source = {}, index = 0) => {
    const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? source.attributes
        : {};
    const merged = {...source, ...attributes};
    const id = source.id ?? merged.id ?? merged.object_type_id ?? merged.company_object_type_id ?? merged.uuid ?? index + 1;
    const fields = arrayFrom(merged.fields ?? merged.custom_fields ?? merged.schema ?? merged.form_fields)
        .map(normalizeObjectTypeField)
        .sort((a, b) => Number(a.order_no ?? 0) - Number(b.order_no ?? 0));
    return {
        ...source,
        ...attributes,
        id,
        name: merged.name ?? merged.title ?? merged.label ?? `Tipo ${index + 1}`,
        description: merged.description ?? '',
        color: merged.color ?? merged.hex_color ?? merged.object_color?.color ?? '#64748b',
        icon: merged.icon ?? merged.icon_name ?? merged.object_icon?.icon ?? 'Box',
        fields,
        defaultImage: merged.default_image ?? merged.default_image_url ?? merged.image ?? null,
    };
};

const normalizeObjectTypes = (payload) => {
    const objectTypes = collectionPayload(payload, 'object_types');
    const types = objectTypes.length ? objectTypes : collectionPayload(payload, 'types');
    return types.map(normalizeObjectType).filter(type => type.id && type.name);
};

const normalizeDepartments = (payload) =>
    collectionPayload(payload, 'departments')
        .map((department, index) => {
            const attributes = department?.attributes && typeof department.attributes === 'object' && !Array.isArray(department.attributes)
                ? department.attributes
                : {};
            const merged = {...department, ...attributes};
            return {
                id: department?.id ?? merged?.id ?? merged?.company_department_id ?? merged?.department_id ?? merged?.uuid ?? index + 1,
                name: merged?.name ?? merged?.title ?? merged?.label ?? `Departamento ${index + 1}`,
                managerIds: arrayFrom(merged?.manager_ids ?? merged?.managerIds ?? merged?.user_ids ?? merged?.userIds)
                    .map(value => String(typeof value === 'object' ? value?.id ?? value?.user_id : value))
                    .filter(Boolean),
            };
        })
        .filter(department => department.id && department.name);

const normalizeFleetManagers = (payload) =>
    collectionPayload(payload, 'users')
        .map((user, index) => {
            const attributes = user?.attributes && typeof user.attributes === 'object' && !Array.isArray(user.attributes)
                ? user.attributes
                : {};
            const merged = {...user, ...attributes};
            const firstName = merged?.first_name ?? merged?.firstName ?? '';
            const lastName = merged?.last_name ?? merged?.lastName ?? '';
            const derivedName = `${firstName} ${lastName}`.trim();
            return {
                ...merged,
                id: user?.id ?? merged?.id ?? merged?.user_id ?? merged?.company_user_id ?? merged?.uuid ?? index + 1,
                name: merged?.name ?? merged?.full_name ?? merged?.display_name ?? derivedName ?? merged?.email ?? `Utilizador ${index + 1}`,
                email: merged?.email ?? '',
                phone: merged?.phone ?? merged?.phone_number ?? merged?.mobile ?? '',
                category: merged?.category ?? merged?.department?.name ?? merged?.company_department?.name ?? merged?.department_name ?? '',
                stats: {
                    vehicles: Number(merged?.stats?.vehicles ?? merged?.vehicles_count ?? 0),
                    incidents: {
                        total: Number(merged?.stats?.incidents?.total ?? merged?.incidents_count ?? 0),
                    },
                    sms: {
                        sent: Number(merged?.stats?.sms?.sent ?? merged?.sms_sent_count ?? 0),
                    },
                    calls: {
                        total: Number(merged?.stats?.calls?.total ?? merged?.calls_count ?? 0),
                    },
                },
            };
        })
        .filter(user => user.id && user.name);

const normalizeDriverContactRules = (payload) => {
    const rules = collectionPayload(payload, 'rules');
    const managerRules = rules.length ? rules : collectionPayload(payload, 'manager_contact_rules');
    return managerRules.map((rule, index) => ({
        ...rule,
        id: rule?.id ?? rule?.manager_contact_rule_id ?? index + 1,
        uuid: rule?.uuid,
        name: rule?.name ?? `Regra ${index + 1}`,
        scope: rule?.scope ?? rule?.type ?? 'category',
        category: rule?.category ?? rule?.department?.name ?? rule?.department_name ?? rule?.department ?? null,
        department_id: rule?.department_id ?? null,
    })).filter(rule => rule.id && rule.name);
};

const normalizeResourceContactRuleGroups = (payload) => {
    const groups = collectionPayload(payload, 'resource_contact_rule_groups');
    const contactRuleGroups = groups.length ? groups : collectionPayload(payload, 'contact_rule_groups');
    const items = contactRuleGroups.length ? contactRuleGroups : collectionPayload(payload, 'groups');
    return items.map((group, index) => {
        const contacts = collectionPayload(group, 'external_contacts').length
            ? collectionPayload(group, 'external_contacts')
            : collectionPayload(group, 'contacts');

        return {
            ...group,
            id: group?.id ?? group?.resource_contact_rule_group_id ?? group?.contact_rule_group_id ?? index + 1,
            uuid: group?.uuid,
            name: group?.name ?? group?.label ?? `Grupo ${index + 1}`,
            description: group?.description ?? '',
            contacts: contacts.map((contact, contactIndex) => {
                const source = contact.external_contact ?? contact.contact ?? contact;
                return {
                    ...source,
                    id: source.id ?? contact.external_contact_id ?? contact.gest_external_contact_id ?? contactIndex + 1,
                    name: source.name ?? `Contacto ${contactIndex + 1}`,
                    phone: source.phone ?? source.phone_number ?? '',
                    email: source.email ?? '',
                    schedule: source.schedule ?? contact.schedule ?? '',
                };
            }),
        };
    }).filter(group => group.id && group.name);
};

const normalizeContactRuleGroupIds = (source = {}, fallback = {}) => {
    const directIds = arrayFrom(
        source.resource_contact_rule_group_ids
        ?? source.contact_rule_group_ids
        ?? source.contact_rules_group_ids
        ?? source.contactRuleGroupIds
        ?? []
    );
    const relationGroups = collectionPayload(source, 'resource_contact_rule_groups').length
        ? collectionPayload(source, 'resource_contact_rule_groups')
        : collectionPayload(source, 'contact_rule_groups');
    const relationIds = relationGroups.map(group => group?.id ?? group?.resource_contact_rule_group_id ?? group?.contact_rule_group_id).filter(Boolean);
    const legacyId = source.resource_contact_rule_group_id ?? source.contact_rule_group_id ?? source.contact_rules_group_id ?? fallback.contactRuleGroupId ?? null;
    const fallbackIds = arrayFrom(fallback.contactRuleGroupIds);
    const ids = directIds.length ? directIds : relationIds.length ? relationIds : fallbackIds.length ? fallbackIds : legacyId ? [legacyId] : [];

    return [...new Set(ids.map(id => String(id)).filter(Boolean))];
};

const normalizeResourceStatuses = (payload) => {
    const statuses = collectionPayload(payload, 'resource_statuses');
    const singularStatuses = statuses.length ? statuses : collectionPayload(payload, 'resource_status');
    const namedStatuses = singularStatuses.length ? singularStatuses : collectionPayload(payload, 'statuses');
    const items = namedStatuses.length ? namedStatuses : collectionPayload(payload, 'resourceStatuses');
    return items.map((resourceStatus, index) => {
        const name = resourceStatus?.name ?? resourceStatus?.label ?? resourceStatus?.title ?? `Estado ${index + 1}`;
        const key = normalizeResourceStatusKey(
            resourceStatus?.slug
            ?? resourceStatus?.key
            ?? resourceStatus?.code
            ?? resourceStatus?.value
            ?? name
        );

        return {
            ...resourceStatus,
            id: resourceStatus?.id ?? resourceStatus?.resource_status_id ?? index + 1,
            name,
            key,
            status: Number(resourceStatus?.status ?? 0),
        };
    }).filter(resourceStatus => resourceStatus.id && resourceStatus.name);
};

const getResourceStatusKey = (resourceStatus = {}) =>
    normalizeResourceStatusKey(resourceStatus.key)
    || normalizeResourceStatusKey(
        resourceStatus.slug
        ?? resourceStatus.code
        ?? resourceStatus.value
        ?? resourceStatus.name
        ?? resourceStatus.label
        ?? resourceStatus.title
    );

const getDefaultActiveResourceStatusId = (resourceStatuses = []) => {
    const activeStatus = resourceStatuses.find(resourceStatus => (
        Number(resourceStatus.status) === 1
        && getResourceStatusKey(resourceStatus) === 'active'
    ));

    return activeStatus?.id ? String(activeStatus.id) : '';
};

const normalizeDriver = (source = null) => {
    if (!source) return null;
    const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? source.attributes
        : {};
    const merged = {...source, ...attributes};
    const firstName = merged.first_name ?? merged.firstName ?? '';
    const lastName = merged.last_name ?? merged.lastName ?? '';
    const derivedName = `${firstName} ${lastName}`.trim();
    return {
        id: source.id ?? merged.id ?? merged.user_id ?? merged.company_user_id ?? merged.driver_id,
        name: merged.name ?? merged.full_name ?? merged.display_name ?? derivedName ?? merged.email ?? '',
        email: merged.email ?? '',
        phone: merged.phone ?? merged.phone_number ?? merged.mobile ?? '',
    };
};

const uniqueDrivers = (drivers = []) => {
    const seen = new Set();
    return drivers.filter(driver => {
        if (!driver?.id || seen.has(String(driver.id))) return false;
        seen.add(String(driver.id));
        return true;
    });
};

const normalizeAssignedDrivers = (source = {}, fallback = {}) => {
    const assignmentRelations = [
        ...arrayFrom(source.user_resource_assignments),
        ...arrayFrom(source.resource_user_assignments),
        ...arrayFrom(source.assignments),
        ...arrayFrom(source.drivers),
        ...arrayFrom(source.users),
        ...arrayFrom(fallback.drivers),
    ];

    const drivers = assignmentRelations.map(item => {
        const relation = item?.user ?? item?.driver ?? item?.company_user ?? item;
        return normalizeDriver(relation);
    });

    return uniqueDrivers(drivers);
};

const resourceIdFromAssignment = (assignment = {}) => {
    const resource = assignment.resource ?? assignment.gest_resource ?? assignment.company_resource ?? {};
    return assignment.resource_id
        ?? assignment.gest_resource_id
        ?? assignment.company_resource_id
        ?? assignment.resourceId
        ?? resource.id
        ?? resource.resource_id
        ?? null;
};

const userIdFromAssignment = (assignment = {}) => {
    const user = assignment.user ?? assignment.driver ?? assignment.company_user ?? {};
    return assignment.user_id
        ?? assignment.company_user_id
        ?? assignment.driver_id
        ?? assignment.userId
        ?? user.id
        ?? user.user_id
        ?? null;
};

const normalizeUserResourceAssignments = (payload, users = []) => {
    const userById = new Map(users.map(user => [String(user.id), user]));
    const assignments = collectionPayload(payload, 'user_resource_assignments');
    const items = assignments.length ? assignments : collectionPayload(payload, 'assignments');

    return items.map(assignment => {
        const userId = userIdFromAssignment(assignment);
        const resourceId = resourceIdFromAssignment(assignment);
        const relationUser = assignment.user ?? assignment.driver ?? assignment.company_user ?? userById.get(String(userId));
        const driver = normalizeDriver(relationUser) ?? userById.get(String(userId)) ?? null;

        return {
            ...assignment,
            userId,
            resourceId,
            driver,
        };
    }).filter(assignment => assignment.userId && assignment.resourceId && assignment.driver);
};

const mergeDriversIntoResources = (resources = [], assignments = []) => {
    const driversByResourceId = assignments.reduce((acc, assignment) => {
        const key = String(assignment.resourceId);
        acc[key] = acc[key] || [];
        acc[key].push(assignment.driver);
        return acc;
    }, {});

    return resources.map(resource => {
        const drivers = uniqueDrivers(driversByResourceId[String(resource.id)] || []);
        return {
            ...resource,
            drivers,
            driver: drivers[0] ?? null,
        };
    });
};

const normalizeTag = (source = null) => {
    if (!source) return null;
    const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? source.attributes
        : {};
    const tag = {...source, ...attributes};
    const id = tag.reference ?? tag.serial_no ?? tag.code ?? tag.id ?? tag.tag_id ?? tag.gest_tag_id;
    if (!id) return null;

    return {
        ...tag,
        id,
        location: tag.location ?? tag.location_name ?? '',
    };
};

const normalizeResourceTag = (source = {}, fallback = {}) => {
    const relationTag = normalizeTag(
        source.tag
        ?? source.gest_tag
        ?? source.resource_tag
        ?? source.assigned_tag
        ?? arrayFrom(source.tags)[0]
    );

    if (relationTag) return relationTag;

    const id = source.tag_id
        ?? source.gest_tag_id
        ?? source.resource_tag_id
        ?? source.tag_reference
        ?? source.tag_code
        ?? fallback.tag?.id;

    return normalizeTag({
        id,
        location: source.tag_location ?? source.location ?? fallback.tag?.location,
    });
};

const normalizeTagStatus = (tag, resource) => {
    if (tag.deactivated_at || tag.gest_tag_deactivation_reason_id || tag.deactivate_reason_id || tag.deactivation_reason_id) return 'inativa';
    if (tag.gest_resource_id || tag.resource_id || resource?.id) return 'atribuída';

    const value = String(tag.status ?? '').toLowerCase();
    const map = {
        0: 'armazém',
        1: 'atribuída',
        2: 'inativa',
        available: 'armazém',
        warehouse: 'armazém',
        armazem: 'armazém',
        assigned: 'atribuída',
        atribuida: 'atribuída',
        active: 'ativa',
        ativa: 'ativa',
        inactive: 'inativa',
        inativa: 'inativa',
    };
    return map[value] || 'armazém';
};

const normalizeDeactivationReason = (tag = {}) => {
    const reason = tag.gest_tag_deactivation_reason ?? tag.deactivation_reason ?? tag.deactivate_reason ?? {};
    const value = String(
        reason.slug
        ?? reason.key
        ?? reason.name
        ?? tag.deactivation_reason
        ?? tag.deactivate_reason
        ?? tag.reason
        ?? tag.reason_name
        ?? tag.gest_tag_deactivation_reason_id
        ?? tag.deactivate_reason_id
        ?? tag.deactivation_reason_id
        ?? ''
    ).trim().toLowerCase();

    if (['1', 'lost'].includes(value) || value.includes('perdid') || value.includes('extravi')) return 'lost';
    if (['2', 'damaged'].includes(value) || value.includes('danific') || value.includes('ilegivel') || value.includes('partid') || value.includes('destru')) return 'damaged';
    return value || 'other';
};

const normalizeDeactivatedTag = (source = {}, index = 0) => {
    const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? source.attributes
        : {};
    const tag = {...source, ...attributes};
    const resource = tag.gest_resource ?? tag.resource ?? {};
    const batch = tag.gest_tag_batch ?? tag.tag_batch ?? tag.batch ?? {};
    const objectType = batch.object_type ?? tag.object_type ?? {};
    const reference = tag.reference ?? tag.serial_no ?? tag.code ?? source.id ?? tag.id ?? `TAG-${index + 1}`;
    const vehicle = resource.reference ?? resource.name ?? resource.title ?? tag.vehicle ?? tag.vehicle_reference ?? '-';

    return {
        id: String(reference),
        deactivatedDate: String(tag.deactivated_at ?? tag.deactivatedAt ?? tag.deactivation_date ?? tag.updated_at ?? tag.created_at ?? '').slice(0, 10),
        reason: normalizeDeactivationReason(tag),
        notes: tag.deactivation_notes ?? tag.deactivate_notes ?? tag.notes ?? tag.reason_notes ?? '',
        vehicle,
        category: objectType.name ?? objectType.title ?? batch.object_type_name ?? tag.category ?? '',
    };
};

const normalizeDeactivatedTags = (payload) =>
    collectionPayload(payload, 'tags')
        .map((source, index) => {
            const attributes = source?.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
                ? source.attributes
                : {};
            const tag = {...source, ...attributes};
            const resource = tag.gest_resource ?? tag.resource ?? {};
            return {source, index, status: normalizeTagStatus(tag, resource)};
        })
        .filter(item => item.status === 'inativa')
        .map(item => normalizeDeactivatedTag(item.source, item.index))
        .filter(tag => tag.id);

const unwrapAttributes = (source = {}) =>
    source?.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? {...source, ...source.attributes}
        : source;

const resolveResourceStatus = (source = {}, fallback = {}, resourceStatuses = []) => {
    const sourceAttributes = source?.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? source.attributes
        : {};
    const relation = unwrapAttributes(source.resource_status ?? source.gest_resource_status ?? source.resourceStatus ?? sourceAttributes.resource_status ?? fallback.resource_status ?? {});
    const resourceStatusId = source.resource_status_id ?? source.gest_resource_status_id ?? sourceAttributes.resource_status_id ?? sourceAttributes.gest_resource_status_id ?? relation?.id ?? fallback.resourceStatusId ?? null;
    const matchedStatus = resourceStatuses.find(resourceStatus => String(resourceStatus.id) === String(resourceStatusId));
    const status = normalizeResourceStatusKey(
        relation?.slug
        ?? relation?.key
        ?? relation?.code
        ?? relation?.value
        ?? relation?.name
        ?? relation?.label
        ?? relation?.title
    )
        || normalizeResourceStatusKey(matchedStatus?.key || matchedStatus?.name)
        || normalizeResourceStatusKey(fallback.status)
        || normalizeResourceStatusKey(source.status ?? sourceAttributes.status)
        || 'active';

    return {resourceStatusId, status};
};

const omitLegacyVehicleFields = (source = {}) => {
    const clean = {...source};
    delete clean.plate;
    delete clean.license_plate;
    delete clean.model;
    delete clean.year;
    return clean;
};

const normalizeResource = (source = {}, fallback = {}, resourceStatuses = []) => {
    const resourceSource = omitLegacyVehicleFields({...fallback, ...source});
    const customFields = safeJson(source.attributes ?? source.custom_fields ?? source.fields ?? source.metadata, {});
    const objectType = source.object_type ?? source.type ?? {};
    const companyManager = normalizeDriver(source.company_manager ?? source.manager);
    const companyDepartment = source.company_department ?? source.department ?? {};
    const id = source.id ?? source.resource_id ?? fallback.id;
    const reference = source.reference ?? customFields.reference ?? fallback.reference ?? '';
    const resourceName = source.name ?? source.title ?? customFields.name ?? customFields.description ?? fallback.name ?? fallback.resourceName ?? '';
    const resourceType = source.resource_type ?? source.resourceType ?? (typeof source.type === 'string' ? source.type : null) ?? customFields.type ?? fallback.resourceType ?? '';
    const contactRuleGroupIds = normalizeContactRuleGroupIds(source, fallback);
    const resourceStatus = resolveResourceStatus(source, fallback, resourceStatuses);
    const drivers = normalizeAssignedDrivers(source, fallback);

    return {
        ...resourceSource,
        id,
        uuid: source.uuid ?? fallback.uuid,
        reference: String(reference || resourceName || `RES-${id || ''}`).trim(),
        type: source.resource_type ?? source.type_name ?? objectType.name ?? objectType.title ?? fallback.type ?? 'resource',
        name: String(resourceName || reference || `Recurso ${id || ''}`).trim(),
        resourceName: String(resourceName || reference || `Recurso ${id || ''}`).trim(),
        resourceType: String(resourceType ?? '').trim(),
        description: source.description ?? customFields.description ?? fallback.description ?? '',
        notes: source.notes ?? customFields.notes ?? fallback.notes ?? '',
        typeId: source.object_type_id ?? objectType.id ?? fallback.typeId ?? null,
        typeName: objectType.name ?? objectType.title ?? source.type_name ?? fallback.typeName ?? '',
        typeColor: objectType.color ?? objectType.hex_color ?? fallback.typeColor ?? '#64748b',
        typeIcon: objectType.icon ?? objectType.icon_name ?? fallback.typeIcon ?? 'Box',
        image: source.image ?? source.image_url ?? source.photo_url ?? fallback.image ?? null,
        color: source.color ?? customFields.color ?? fallback.color ?? '',
        category: source.category ?? companyDepartment?.name ?? companyDepartment?.title ?? source.company_department_name ?? source.department_name ?? fallback.category ?? 'Sem categoria',
        departmentId: source.company_department_id ?? source.department_id ?? companyDepartment?.id ?? fallback.departmentId ?? null,
        status: resourceStatus.status,
        resourceStatusId: resourceStatus.resourceStatusId,
        fleetManagerId: source.company_manager_id ?? source.fleet_manager_id ?? source.manager_id ?? fallback.fleetManagerId ?? null,
        fleetManager: companyManager ?? fallback.fleetManager ?? null,
        drivers,
        driver: drivers[0] ?? null,
        tag: normalizeResourceTag(source, fallback),
        contactRuleGroupIds,
        contactRuleGroupId: contactRuleGroupIds[0] ?? source.resource_contact_rule_group_id ?? source.contact_rule_group_id ?? source.contact_rules_group_id ?? fallback.contactRuleGroupId ?? null,
        driverContactRuleId: source.driver_contact_rule_id ?? source.manager_contact_rule_id ?? fallback.driverContactRuleId ?? null,
        lastActivity: source.last_activity ?? source.updated_at ?? fallback.lastActivity ?? '',
        customFields,
    };
};

const normalizeResources = (payload, resourceStatuses = []) => {
    const resources = collectionPayload(payload, 'resources');
    const items = resources.length ? resources : collectionPayload(payload, 'vehicles');
    return items.map(resource => normalizeResource(resource, {}, resourceStatuses)).filter(resource => resource.id);
};

const normalizeIncidentStatus = (status) => {
    const value = String(status ?? '').toLowerCase();
    const map = {
        0: 'pending',
        1: 'in_progress',
        2: 'resolved',
        pending: 'pending',
        pendente: 'pending',
        open: 'pending',
        in_progress: 'in_progress',
        progress: 'in_progress',
        em_progresso: 'in_progress',
        resolved: 'resolved',
        resolvida: 'resolved',
        closed: 'resolved',
    };
    return map[value] || value || 'pending';
};

const formatIncidentDate = (value) => {
    if (!value) return '';
    return String(value).replace('T', ' ').slice(0, 16);
};

const getIncidentTagKeys = (occurrence = {}) => {
    const resource = occurrence.resource ?? occurrence.vehicle ?? occurrence.object ?? {};
    const tag = occurrence.tag ?? resource.tag ?? {};
    return [
        tag.id,
        tag.code,
        tag.tag_code,
        occurrence.tag_id,
        occurrence.tag_code,
    ].map(key => String(key ?? '').trim()).filter(Boolean);
};

const normalizeTagIncident = (source = {}, index = 0) => {
    const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? source.attributes
        : {};
    const occurrence = {...source, ...attributes};
    const type = occurrence.occurrence_type ?? occurrence.type ?? {};
    const reporter = occurrence.reporter ?? occurrence.reported_by ?? occurrence.user ?? occurrence.created_by ?? {};

    return {
        id: occurrence.id ?? occurrence.code ?? occurrence.reference ?? `incident-${index + 1}`,
        date: formatIncidentDate(occurrence.reported_at ?? occurrence.occurred_at ?? occurrence.created_at ?? occurrence.date),
        type: type.name ?? type.title ?? occurrence.type_name ?? occurrence.occurrence_type_name ?? occurrence.title ?? 'Ocorrência',
        status: normalizeIncidentStatus(occurrence.status),
        description: occurrence.description ?? occurrence.notes ?? occurrence.message ?? occurrence.details ?? '',
        reportedBy: reporter.name ?? reporter.full_name ?? occurrence.reported_by_name ?? occurrence.reporter_name ?? occurrence.origin ?? occurrence.source ?? '-',
    };
};

const normalizeTagIncidents = (payload) => {
    const occurrences = collectionPayload(payload, 'occurrences');
    const items = occurrences.length ? occurrences : collectionPayload(payload, 'incidents');

    return items.reduce((acc, source, index) => {
        const incident = normalizeTagIncident(source, index);
        getIncidentTagKeys({...source, ...(source.attributes || {})}).forEach(tagKey => {
            acc[tagKey] = acc[tagKey] || [];
            acc[tagKey].push(incident);
        });
        return acc;
    }, {});
};

const resourcePayload = (resource = {}) => ({
    object_type_id: toApiId(resource.typeId),
    reference: String(resource.reference ?? '').trim(),
    name: String(resource.resourceName ?? resource.name ?? '').trim() || null,
    type: String(resource.resourceType ?? '').trim() || null,
    description: String(resource.description ?? '').trim() || null,
    notes: String(resource.notes ?? '').trim() || null,
    company_department_id: toApiId(resource.departmentId),
    company_manager_id: toApiId(resource.fleetManagerId),
    has_manager: Boolean(resource.fleetManagerId),
    attributes: resource.attributes ?? resource.customFields ?? {},
    image: resource.image ?? null,
    resource_status_id: toApiId(resource.resourceStatusId),
});

const resourceContactRuleGroupsPayload = (resource = {}, contactRuleGroupIds = []) => {
    const ids = contactRuleGroupIds.map(toApiId).filter(id => id !== null);

    return {
        ...resourceIdentityPayload(resource),
        contact_rule_group_ids: ids,
        resource_contact_rule_group_ids: ids,
    };
};

const resourceIdentityPayload = (resource = {}, prefix = 'resource') => ({
    [`${prefix}_id`]: toApiId(resource.id),
    [`${prefix}_uuid`]: resource.uuid ?? null,
});

const userResourceAssignmentPayload = (userId, resourceId) => ({
    user_id: toApiId(userId),
    resource_id: toApiId(resourceId),
});

const TagIdDisplay = ({tagId, size = 'default'}) => {
    const value = String(tagId || '');
    const letters = value.substring(0, 2);
    const numbers = value.substring(2);
    const sizeClasses = {small: 'text-sm', default: 'text-base', large: 'text-lg', xlarge: 'text-xl'};
    return (
        <div className={`font-mono font-bold ${sizeClasses[size]} inline-flex items-center`}>
            {/*
            <span className="bg-purple-600 text-white px-1.5 py-0.5 rounded-l">{letters}</span>
            <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-r border border-l-0 border-purple-300">{numbers}</span>
            */}
            <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded border-purple-300">{value}</span>
        </div>
    );
};

export const FleetDashboard = () => {
    const navigate = useNavigate();
    const {can} = usePermissions();
    const canReadResources = can(PERMISSIONS.RESOURCE_READ);
    const canCreateResources = can(PERMISSIONS.RESOURCE_CREATE);
    const canUpdateResources = can(PERMISSIONS.RESOURCE_UPDATE);
    const canDeleteResources = can(PERMISSIONS.RESOURCE_DELETE);
    const [vehicles, setVehicles] = useState([]);
    const [objectTypes, setObjectTypes] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [resourceStatuses, setResourceStatuses] = useState([]);
    const [fleetManagerOptions, setFleetManagerOptions] = useState([]);
    const [driverOptions, setDriverOptions] = useState([]);
    const [driverRuleOptions, setDriverRuleOptions] = useState([]);
    const [ruleGroups, setRuleGroups] = useState([]);
    const [tagIncidents, setTagIncidents] = useState({});
    const [availableTags, setAvailableTags] = useState([]);
    const [deactivatedTags, setDeactivatedTags] = useState([]);
    const [resourcesLoading, setResourcesLoading] = useState(true);
    const [resourcesError, setResourcesError] = useState('');
    const [objectTypesLoading, setObjectTypesLoading] = useState(true);
    const [objectTypesError, setObjectTypesError] = useState('');
    const [departmentsLoading, setDepartmentsLoading] = useState(true);
    const [departmentsError, setDepartmentsError] = useState('');
    const [resourceStatusesLoading, setResourceStatusesLoading] = useState(true);
    const [resourceStatusesError, setResourceStatusesError] = useState('');
    const [fleetManagersLoading, setFleetManagersLoading] = useState(true);
    const [fleetManagersError, setFleetManagersError] = useState('');
    const [driverRulesLoading, setDriverRulesLoading] = useState(true);
    const [driverRulesError, setDriverRulesError] = useState('');
    const [ruleGroupsLoading, setRuleGroupsLoading] = useState(true);
    const [ruleGroupsError, setRuleGroupsError] = useState('');
    const [tagIncidentsLoading, setTagIncidentsLoading] = useState(true);
    const [tagIncidentsError, setTagIncidentsError] = useState('');
    const [deactivatedTagsError, setDeactivatedTagsError] = useState('');
    const [submittingResource, setSubmittingResource] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

    // Dialogs
    const [isVehicleDetailOpen, setIsVehicleDetailOpen] = useState(false);
    const [isEditVehicleOpen, setIsEditVehicleOpen] = useState(false);
    const [isCreateVehicleOpen, setIsCreateVehicleOpen] = useState(false);
    const [isAssignDriverOpen, setIsAssignDriverOpen] = useState(false);
    const [isDeactivateTagOpen, setIsDeactivateTagOpen] = useState(false);
    const [isTagHistoryOpen, setIsTagHistoryOpen] = useState(false);
    const [isSwapDriverOpen, setIsSwapDriverOpen] = useState(false);
    const [isCreateRuleGroupOpen, setIsCreateRuleGroupOpen] = useState(false);
    const [isEditRuleGroupOpen, setIsEditRuleGroupOpen] = useState(false);
    const [isAssignRuleOpen, setIsAssignRuleOpen] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedTag, setSelectedTag] = useState(null);
    const [selectedRuleGroup, setSelectedRuleGroup] = useState(null);

    // Forms
    const [editReference, setEditReference] = useState('');
    const [editResourceName, setEditResourceName] = useState('');
    const [editResourceType, setEditResourceType] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [editResourceStatusId, setEditResourceStatusId] = useState('');
    const [editFleetManagerId, setEditFleetManagerId] = useState('');
    const [newColor, setNewColor] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [newReference, setNewReference] = useState('');
    const [newResourceName, setNewResourceName] = useState('');
    const [newResourceType, setNewResourceType] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [newNotes, setNewNotes] = useState('');
    const [newStatus, setNewStatus] = useState('active');
    const [newResourceStatusId, setNewResourceStatusId] = useState('');
    const [newFleetManagerId, setNewFleetManagerId] = useState('');
    // Object-type-based create flow
    const [createStep, setCreateStep] = useState(1); // 1 = pick type, 2 = fill fields
    const [createTypeId, setCreateTypeId] = useState(null);
    const [createFieldValues, setCreateFieldValues] = useState({});
    const [editFieldValues, setEditFieldValues] = useState({});
    const [newImage, setNewImage] = useState(null); // pedido c — foto do veículo/equipamento
    const [editImage, setEditImage] = useState(null);
    const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
    const [isEditImagePickerOpen, setIsEditImagePickerOpen] = useState(false);
    const [newDriverRuleChoice, setNewDriverRuleChoice] = useState('inherit');
    const createStatusInitializedRef = useRef(false);
    const [selectedDriverIds, setSelectedDriverIds] = useState([]);
    const [isDriverPickerOpen, setIsDriverPickerOpen] = useState(false);
    const [deactivateReason, setDeactivateReason] = useState('');
    const [deactivateNotes, setDeactivateNotes] = useState('');
    const [swapVehicleA, setSwapVehicleA] = useState('');
    const [swapVehicleB, setSwapVehicleB] = useState('');
    // Rule group form
    const [rgName, setRgName] = useState('');
    const [rgDesc, setRgDesc] = useState('');
    const [rgContacts, setRgContacts] = useState([]);
    const [rcName, setRcName] = useState('');
    const [rcPhone, setRcPhone] = useState('');
    const [rcEmail, setRcEmail] = useState('');
    const [rcSchedule, setRcSchedule] = useState('');
    const [assignRuleGroupIds, setAssignRuleGroupIds] = useState([]);

    const requireResourcePermission = (allowed) => {
        if (allowed) return true;
        toast.error('Nao tem permissão para executar esta ação.');
        return false;
    };

    useEffect(() => {
        let cancelled = false;

        const loadFleetData = async () => {
            if (!canReadResources) {
                setResourcesLoading(false);
                setObjectTypesLoading(false);
                setDepartmentsLoading(false);
                setResourceStatusesLoading(false);
                setFleetManagersLoading(false);
                setDriverRulesLoading(false);
                setRuleGroupsLoading(false);
                setTagIncidentsLoading(false);
                setResourcesError('Nao tem permissão para consultar recursos.');
                return;
            }

            setResourcesLoading(true);
            setObjectTypesLoading(true);
            setDepartmentsLoading(true);
            setResourceStatusesLoading(true);
            setFleetManagersLoading(true);
            setDriverRulesLoading(true);
            setRuleGroupsLoading(true);
            setTagIncidentsLoading(true);
            setResourcesError('');
            setObjectTypesError('');
            setDepartmentsError('');
            setResourceStatusesError('');
            setFleetManagersError('');
            setDriverRulesError('');
            setRuleGroupsError('');
            setTagIncidentsError('');
            setDeactivatedTagsError('');

            try {
                const [resourcesResponse, objectTypesResponse, departmentsResponse, resourceStatusesResponse, usersResponse, assignmentsResponse, driverRulesResponse, ruleGroupsResponse, occurrencesResponse, tagsResponse] = await Promise.all([
                    api.get(resourceEndpoints.list),
                    api.get(objectTypeEndpoints.list).catch(error => ({data: [], error})),
                    api.get(departmentEndpoints.list).catch(error => ({data: [], error})),
                    api.get(resourceStatusEndpoints.list).catch(error => ({data: [], error})),
                    api.get(userEndpoints.list).catch(error => ({data: [], error})),
                    api.get(userResourceAssignmentEndpoints.list).catch(error => ({data: [], error})),
                    api.get(driverContactRuleEndpoints.list).catch(error => ({data: [], error})),
                    api.get(resourceContactRuleGroupEndpoints.list).catch(error => ({data: [], error})),
                    api.get(occurrenceEndpoints.list).catch(error => ({data: [], error})),
                    api.get(tagEndpoints.list).catch(error => ({data: [], error})),
                ]);

                if (!cancelled) {
                    const normalizedResourceStatuses = normalizeResourceStatuses(resourceStatusesResponse.data);
                    const users = normalizeFleetManagers(usersResponse.data);
                    const resources = normalizeResources(resourcesResponse.data, normalizedResourceStatuses);
                    const assignments = normalizeUserResourceAssignments(assignmentsResponse.data, users);
                    setVehicles(mergeDriversIntoResources(resources, assignments));
                    setObjectTypes(normalizeObjectTypes(objectTypesResponse.data));
                    setDepartments(normalizeDepartments(departmentsResponse.data));
                    setResourceStatuses(normalizedResourceStatuses);
                    setFleetManagerOptions(users);
                    setDriverOptions(users);
                    setDriverRuleOptions(normalizeDriverContactRules(driverRulesResponse.data));
                    setRuleGroups(normalizeResourceContactRuleGroups(ruleGroupsResponse.data));
                    setTagIncidents(normalizeTagIncidents(occurrencesResponse.data));
                    setAvailableTags(normalizeAvailableTags(tagsResponse.data));
                    setDeactivatedTags(normalizeDeactivatedTags(tagsResponse.data));
                    if (objectTypesResponse.error) {
                        setObjectTypesError(getApiErrorMessage(objectTypesResponse.error, 'Nao foi possível carregar os tipos de objeto.'));
                    }
                    if (departmentsResponse.error) {
                        setDepartmentsError(getApiErrorMessage(departmentsResponse.error, 'Nao foi possível carregar os departamentos.'));
                    }
                    if (resourceStatusesResponse.error) {
                        setResourceStatusesError(getApiErrorMessage(resourceStatusesResponse.error, 'Nao foi possível carregar os estados dos recursos.'));
                    }
                    if (usersResponse.error) {
                        setFleetManagersError(getApiErrorMessage(usersResponse.error, 'Nao foi possível carregar os utilizadores.'));
                    }
                    if (assignmentsResponse.error) {
                        setResourcesError(getApiErrorMessage(assignmentsResponse.error, 'Nao foi possível carregar as atribuições de condutores.'));
                    }
                    if (driverRulesResponse.error) {
                        setDriverRulesError(getApiErrorMessage(driverRulesResponse.error, 'Nao foi possível carregar as regras de contacto ao condutor.'));
                    }
                    if (ruleGroupsResponse.error) {
                        setRuleGroupsError(getApiErrorMessage(ruleGroupsResponse.error, 'Nao foi possível carregar as regras tags sem responsável.'));
                    }
                    if (occurrencesResponse.error) {
                        setTagIncidents({});
                        setTagIncidentsError(getApiErrorMessage(occurrencesResponse.error, 'Nao foi possível carregar o histórico de ocorrências.'));
                    }
                    if (tagsResponse.error) {
                        setAvailableTags([]);
                        setDeactivatedTags([]);
                        setDeactivatedTagsError(getApiErrorMessage(tagsResponse.error, 'Nao foi possível carregar as tags em baixa.'));
                    }
                }
            } catch (error) {
                if (!cancelled) {
                    setVehicles([]);
                    setAvailableTags([]);
                    setTagIncidents({});
                    setDeactivatedTags([]);
                    setResourcesError(getApiErrorMessage(error, 'Nao foi possível carregar os recursos.'));
                }
            } finally {
                if (!cancelled) {
                    setResourcesLoading(false);
                    setObjectTypesLoading(false);
                    setDepartmentsLoading(false);
                    setResourceStatusesLoading(false);
                    setFleetManagersLoading(false);
                    setDriverRulesLoading(false);
                    setRuleGroupsLoading(false);
                    setTagIncidentsLoading(false);
                }
            }
        };

        loadFleetData();

        return () => {
            cancelled = true;
        };
    }, [canReadResources]);

    const getFleetManager = (id) => fleetManagerOptions.find(m => String(m.id) === String(id));
    const getRuleGroup = (id) => ruleGroups.find(rg => String(rg.id) === String(id));
    const getRuleGroups = (ids = []) => arrayFrom(ids).map(id => getRuleGroup(id)).filter(Boolean);
    const getVehicleRuleGroupIds = (vehicle = {}) => normalizeContactRuleGroupIds(vehicle);
    const getVehicleRuleGroups = (vehicle = {}) => getRuleGroups(getVehicleRuleGroupIds(vehicle));
    const getObjectType = (id) => objectTypes.find(t => String(t.id) === String(id));
    const getDepartment = (id) => departments.find(department => String(department.id) === String(id));
    const getDepartmentManagerIds = (departmentId) => new Set((getDepartment(departmentId)?.managerIds || []).map(String));
    const getFleetManagersForDepartment = (departmentId) => {
        const managerIds = getDepartmentManagerIds(departmentId);
        if (!departmentId || managerIds.size === 0) return [];
        return fleetManagerOptions.filter(manager => managerIds.has(String(manager.id)));
    };
    const isManagerInDepartment = (managerId, departmentId) =>
        Boolean(managerId) && getDepartmentManagerIds(departmentId).has(String(managerId));
    const newFleetManagerOptions = getFleetManagersForDepartment(newCategory);
    const editFleetManagerOptions = getFleetManagersForDepartment(editCategory);
    const getVehicleDrivers = (vehicle = {}) => {
        const source = vehicle || {};
        return uniqueDrivers(arrayFrom(source.drivers).length ? source.drivers : (source.driver ? [source.driver] : []));
    };
    const hasVehicleDrivers = (vehicle = {}) => getVehicleDrivers(vehicle).length > 0;
    const formatDriverOption = (driver = {}) => [
        driver.name,
        driver.nickname,
        driver.phone,
    ].filter(Boolean).join(' . ');
    const selectedDriverId = selectedDriverIds[0] || 'none';
    const selectedDriver = selectedDriverId === 'none'
        ? null
        : driverOptions.find(driver => String(driver.id) === String(selectedDriverId));
    const selectedDriverLabel = selectedDriver ? formatDriverOption(selectedDriver) : 'Sem condutor';
    const activeResourceStatuses = resourceStatuses.filter(resourceStatus => Number(resourceStatus.status) === 1);
    const defaultActiveResourceStatusId = getDefaultActiveResourceStatusId(resourceStatuses);
    const getResourceStatus = (id) => resourceStatuses.find(resourceStatus => String(resourceStatus.id) === String(id));
    const getDriverRule = (id) => driverRuleOptions.find(rule => String(rule.id) === String(id));
    const getVehicleObjectType = (vehicle = {}) => getObjectType(vehicle.typeId);
    const getVehicleDetailFieldValues = (vehicle = {}) => ({
        attributes: vehicle.customFields || vehicle.attributes || {},
    });
    const formatDetailValue = (value, field = null) => {
        if (field?.type === 'select') {
            const option = (field.options || []).find(opt => String(opt.value) === String(value));
            return option?.label || value || '-';
        }

        if (field?.type === 'coords') {
            if (!value?.lat && !value?.lng) return '-';
            return [value?.lat, value?.lng].filter(Boolean).join(', ');
        }

        if (value === undefined || value === null || value === '') return '-';
        if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };
    const DetailField = ({label, value, className = '', valueClassName = ''}) => (
        <div className={`min-w-0 ${className}`}>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
            <p className={`mt-1 text-sm text-slate-900 break-words ${valueClassName}`}>{formatDetailValue(value)}</p>
        </div>
    );

    useEffect(() => {
        if (!isCreateVehicleOpen) {
            createStatusInitializedRef.current = false;
            return;
        }

        if (createStatusInitializedRef.current || newResourceStatusId || !defaultActiveResourceStatusId) return;
        createStatusInitializedRef.current = true;
        setNewResourceStatusId(defaultActiveResourceStatusId);
    }, [defaultActiveResourceStatusId, isCreateVehicleOpen, newResourceStatusId]);

    const formatDuration = (s) => {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    const filteredVehicles = vehicles.filter(v => {
        const driverSearch = getVehicleDrivers(v).map(driver => driver.name).join(' ').toLowerCase();
        const ms = v.name.toLowerCase().includes(searchTerm.toLowerCase()) || v.reference.toLowerCase().includes(searchTerm.toLowerCase()) || driverSearch.includes(searchTerm.toLowerCase());
        return ms && (filterCategory === 'all' || v.category === filterCategory) && (filterStatus === 'all' || v.status === filterStatus);
    });
    const vehiclesWithoutDriver = vehicles.filter(v => !hasVehicleDrivers(v));
    const handleViewVehicle = (v) => {
        if (!requireResourcePermission(canReadResources)) return;
        setSelectedVehicle(v);
        setIsVehicleDetailOpen(true);
    };

    const handleEditVehicle = (v) => {
        if (!requireResourcePermission(canUpdateResources)) return;
        setSelectedVehicle(v);
        setEditReference(v.reference || '');
        setEditResourceName(v.resourceName || '');
        setEditResourceType(v.resourceType || '');
        setEditDescription(v.description || '');
        setEditNotes(v.notes || '');
        setEditCategory(v.departmentId?.toString() || '');
        setEditResourceStatusId(v.resourceStatusId?.toString() || '');
        setEditFleetManagerId(isManagerInDepartment(v.fleetManagerId, v.departmentId) ? v.fleetManagerId?.toString() : '');
        setEditFieldValues({attributes: v.customFields || {}});
        setEditImage(v.image || null);
        setIsEditVehicleOpen(true);
    };
    const handleNewCategoryChange = (departmentId) => {
        setNewCategory(departmentId);
        if (!isManagerInDepartment(newFleetManagerId, departmentId)) {
            setNewFleetManagerId('');
        }
    };
    const handleEditCategoryChange = (departmentId) => {
        setEditCategory(departmentId);
        if (!isManagerInDepartment(editFleetManagerId, departmentId)) {
            setEditFleetManagerId('');
        }
    };
    const handleOpenCreateVehicle = () => {
        if (!requireResourcePermission(canCreateResources)) return;
        setCreateStep(1);
        setCreateTypeId(null);
        setCreateFieldValues({});
        setNewCategory('');
        setNewReference('');
        setNewResourceName('');
        setNewResourceType('');
        setNewDescription('');
        setNewNotes('');
        setNewStatus('active');
        createStatusInitializedRef.current = Boolean(defaultActiveResourceStatusId);
        setNewResourceStatusId(defaultActiveResourceStatusId);
        setNewFleetManagerId('');
        setNewDriverRuleChoice('inherit');
        setNewImage(null);
        setIsCreateVehicleOpen(true);
    };
    const handlePickType = (typeId) => {
        if (!requireResourcePermission(canCreateResources)) return;
        setCreateTypeId(typeId);
        // pre-fill defaults
        const type = getObjectType(typeId);
        let defaults = {attributes: {}};
        (type?.fields || []).forEach(f => {
            defaults = setPathValue(defaults, f.valuePath, f.default_value ?? (f.type === 'coords' ? {lat: '', lng: ''} : ''));
        });
        setCreateFieldValues(defaults);
        setCreateStep(2);
    };
    const handleCreateVehicle = async () => {
        if (!requireResourcePermission(canCreateResources)) return;
        const type = getObjectType(createTypeId);
        if (!type) {
            toast.error('Selecione um tipo de objeto.');
            return;
        }
        const reference = newReference.trim();
        if (!reference) {
            toast.error('Referência obrigatória.');
            return;
        }
        const resourceName = newResourceName.trim();
        if (!resourceName) {
            toast.error('Nome obrigatório.');
            return;
        }
        const resourceType = newResourceType.trim();
        if (!resourceType) {
            toast.error('Tipo obrigatório.');
            return;
        }
        if (!newCategory) {
            toast.error('Selecione uma categoria.');
            return;
        }
        if (newFleetManagerId && !isManagerInDepartment(newFleetManagerId, newCategory)) {
            toast.error('Selecione um gestor associado a categoria.');
            return;
        }
        // Validate required fields
        for (const f of type.fields) {
            if (!f.required) continue;
            const v = getPathValue(createFieldValues, f.valuePath);
            const empty = f.type === 'coords' ? (!v?.lat || !v?.lng) : (!v || String(v).trim() === '');
            if (empty) {
                toast.error(`Campo obrigatório: ${f.label}`);
                return;
            }
        }
        // Use the resource reference as the display identifier.
        const attributes = createFieldValues.attributes || {};
        const department = getDepartment(newCategory);
        const resource = {
            reference,
            name: resourceName,
            resourceName,
            resourceType,
            description: newDescription.trim(),
            notes: newNotes.trim(),
            type: type.name.toLowerCase() === 'carro' ? 'veiculo' : type.name.toLowerCase(),
            typeId: type.id,
            typeName: type.name,
            typeColor: type.color,
            typeIcon: type.icon,
            image: newImage || type.defaultImage || null,
            color: attributes.color || '',
            category: department?.name || null,
            departmentId: newCategory ? parseInt(newCategory) : null,
            status: newStatus || 'active',
            resourceStatusId: newResourceStatusId ? parseInt(newResourceStatusId) : null,
            fleetManagerId: newFleetManagerId ? parseInt(newFleetManagerId) : null,
            driver: null, tag: null, contactRuleGroupId: null, contactRuleGroupIds: [],
            driverContactRuleId: newDriverRuleChoice === 'inherit' ? null : parseInt(newDriverRuleChoice),
            lastActivity: new Date().toISOString().slice(0, 16).replace('T', ' '),
            customFields: attributes,
            attributes,
        };
        setSubmittingResource(true);
        try {
            const response = await api.post(resourceEndpoints.create, resourcePayload(resource));
            const created = normalizeResource(response.data?.data ?? response.data?.resource ?? response.data, {
                id: Date.now(),
                ...resource,
            }, resourceStatuses);
            setVehicles(p => [created, ...p]);
            setIsCreateVehicleOpen(false);
            toast.success(`${type.name} "${created.name}" criado!`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Nao foi possível criar o recurso.'));
        } finally {
            setSubmittingResource(false);
        }
    };
    const handleSaveVehicle = async () => {
        if (!requireResourcePermission(canUpdateResources)) return;
        if (!selectedVehicle) return;

        const reference = editReference.trim();
        if (!reference) {
            toast.error('Referência obrigatória.');
            return;
        }
        const resourceName = editResourceName.trim();
        if (!resourceName) {
            toast.error('Nome obrigatório.');
            return;
        }
        const resourceType = editResourceType.trim();
        if (!resourceType) {
            toast.error('Tipo obrigatório.');
            return;
        }
        if (!editCategory) {
            toast.error('Selecione uma categoria.');
            return;
        }
        if (editFleetManagerId && !isManagerInDepartment(editFleetManagerId, editCategory)) {
            toast.error('Selecione um gestor associado a categoria.');
            return;
        }
        const type = getObjectType(selectedVehicle.typeId);
        for (const f of type?.fields || []) {
            if (!f.required) continue;
            const v = getPathValue(editFieldValues, f.valuePath);
            const empty = f.type === 'coords' ? (!v?.lat || !v?.lng) : (!v || String(v).trim() === '');
            if (empty) {
                toast.error(`Campo obrigatório: ${f.label}`);
                return;
            }
        }
        const attributes = editFieldValues.attributes || {};
        const department = getDepartment(editCategory);
        const next = {
            ...selectedVehicle,
            reference,
            name: resourceName,
            resourceName,
            resourceType,
            description: editDescription.trim(),
            notes: editNotes.trim(),
            color: attributes.color || selectedVehicle.color || '',
            category: department?.name || null,
            departmentId: editCategory ? parseInt(editCategory) : null,
            resourceStatusId: editResourceStatusId ? parseInt(editResourceStatusId) : null,
            fleetManagerId: editFleetManagerId ? parseInt(editFleetManagerId) : null,
            image: editImage || type?.defaultImage || null,
            customFields: attributes,
            attributes,
        };
        setSubmittingResource(true);
        try {
            const response = await api.post(resourceEndpoints.update, {
                id: selectedVehicle.id,
                uuid: selectedVehicle.uuid,
                ...resourcePayload(next),
            });
            const updated = normalizeResource(response.data?.data ?? response.data?.resource ?? response.data, next, resourceStatuses);
            setVehicles(p => p.map(v => v.id === selectedVehicle.id ? updated : v));
            setSelectedVehicle(updated);
            setIsEditVehicleOpen(false);
            toast.success('Veículo atualizado!');
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Nao foi possível atualizar o recurso.'));
        } finally {
            setSubmittingResource(false);
        }
    };
    const handleOpenAssignDriver = (v) => {
        if (!requireResourcePermission(canUpdateResources)) return;
        setSelectedVehicle(v);
        setSelectedDriverIds(getVehicleDrivers(v).slice(0, 1).map(driver => String(driver.id)));
        setIsAssignDriverOpen(true);
    };

    const handleSelectedDriverChange = (driverId) => {
        setSelectedDriverIds(driverId === 'none' ? [] : [driverId]);
    };

    const fetchResourceWithDrivers = async (resource) => {
        const response = await api.get(userResourceAssignmentEndpoints.list, {
            params: {resource_id: resource.id},
        });
        const assignments = normalizeUserResourceAssignments(response.data, driverOptions);
        return mergeDriversIntoResources([resource], assignments)[0];
    };

    const updateResource = async (resource, patch) => {
        if (!canUpdateResources) throw new Error('Nao tem permissão para executar esta ação.');
        const next = {...resource, ...patch};
        const response = await api.post(resourceEndpoints.update, {
            id: resource.id,
            uuid: resource.uuid,
            ...resourcePayload(next),
        });
        return normalizeResource(response.data?.data ?? response.data?.resource ?? response.data, next, resourceStatuses);
    };

    const handleAssignDriver = async () => {
        if (!requireResourcePermission(canUpdateResources)) return;
        if (!selectedVehicle) return;
        setSubmittingResource(true);
        try {
            const currentDriverIds = getVehicleDrivers(selectedVehicle).map(driver => String(driver.id));
            const nextDriverIds = selectedDriverIds.map(String);
            const removedDriverIds = currentDriverIds.filter(id => !nextDriverIds.includes(id));
            const addedDriverIds = nextDriverIds.filter(id => !currentDriverIds.includes(id));

            await Promise.all(removedDriverIds.map(userId =>
                api.post(userResourceAssignmentEndpoints.detach, userResourceAssignmentPayload(userId, selectedVehicle.id))
            ));

            await Promise.all(addedDriverIds.map(userId =>
                api.post(userResourceAssignmentEndpoints.attach, userResourceAssignmentPayload(userId, selectedVehicle.id))
            ));

            const updated = await fetchResourceWithDrivers(selectedVehicle);
            setVehicles(p => p.map(v => v.id === selectedVehicle.id ? updated : v));
            setSelectedVehicle(updated);
            setIsAssignDriverOpen(false);
            const drivers = getVehicleDrivers(updated);
            toast.success(drivers.length ? 'Condutores atualizados!' : 'Condutores removidos');
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Nao foi possível atualizar o condutor do recurso.'));
        } finally {
            setSubmittingResource(false);
        }
    };
    // SWAP
    const handleOpenSwapDriver = () => {
        if (!requireResourcePermission(canUpdateResources)) return;
        setSwapVehicleA('');
        setSwapVehicleB('');
        setIsSwapDriverOpen(true);
    };
    const handleSwapDrivers = async () => {
        if (!requireResourcePermission(canUpdateResources)) return;
        const a = vehicles.find(v => String(v.id) === String(swapVehicleA));
        const b = vehicles.find(v => String(v.id) === String(swapVehicleB));
        if (!a || !b) {
            toast.error('Selecione dois veículos.');
            return;
        }
        setSubmittingResource(true);
        try {
            const driversA = getVehicleDrivers(a);
            const driversB = getVehicleDrivers(b);
            const detachRequests = [
                ...driversA.map(driver => api.post(userResourceAssignmentEndpoints.detach, userResourceAssignmentPayload(driver.id, a.id))),
                ...driversB.map(driver => api.post(userResourceAssignmentEndpoints.detach, userResourceAssignmentPayload(driver.id, b.id))),
            ];
            const attachRequests = [
                ...driversB.map(driver => api.post(userResourceAssignmentEndpoints.attach, userResourceAssignmentPayload(driver.id, a.id))),
                ...driversA.map(driver => api.post(userResourceAssignmentEndpoints.attach, userResourceAssignmentPayload(driver.id, b.id))),
            ];

            await Promise.all(detachRequests);
            await Promise.all(attachRequests);

            const [updatedA, updatedB] = await Promise.all([
                fetchResourceWithDrivers(a),
                fetchResourceWithDrivers(b),
            ]);
            setVehicles(p => p.map(v => {
                if (v.id === a.id) return updatedA;
                if (v.id === b.id) return updatedB;
                return v;
            }));
            setIsSwapDriverOpen(false);
            toast.success(`Condutores trocados entre ${a.reference} e ${b.reference}!`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Nao foi possível trocar os condutores.'));
        } finally {
            setSubmittingResource(false);
        }
    };
    // RULE GROUPS
    const handleOpenCreateRuleGroup = () => {
        setRgName('');
        setRgDesc('');
        setRgContacts([]);
        setRcName('');
        setRcPhone('');
        setRcEmail('');
        setRcSchedule('');
        setIsCreateRuleGroupOpen(true);
    };
    const handleAddRgContact = () => {
        if (!rcName.trim() || !rcPhone.trim()) {
            toast.error('Nome e telefone obrigatórios.');
            return;
        }
        if (!/^\d{9}$/.test(rcPhone.trim())) {
            toast.error('O telefone deve ter 9 dígitos.');
            return;
        }
        setRgContacts(p => [...p, {id: Date.now(), name: rcName.trim(), phone: rcPhone.trim(), email: rcEmail.trim(), schedule: rcSchedule.trim() || '24h'}]);
        setRcName('');
        setRcPhone('');
        setRcEmail('');
        setRcSchedule('');
    };
    const handleRemoveRgContact = (id) => setRgContacts(p => p.filter(c => c.id !== id));
    const handleSaveRuleGroup = () => {
        if (!rgName.trim()) {
            toast.error('Nome do grupo obrigatório.');
            return;
        }
        if (rgContacts.length === 0) {
            toast.error('Adicione pelo menos um contacto.');
            return;
        }
        const newRg = {id: Math.max(...ruleGroups.map(r => r.id), 0) + 1, name: rgName.trim(), description: rgDesc.trim(), contacts: rgContacts};
        setRuleGroups(p => [...p, newRg]);
        setIsCreateRuleGroupOpen(false);
        toast.success(`Grupo "${newRg.name}" criado!`);
    };
    const handleOpenEditRuleGroup = (rg) => {
        setSelectedRuleGroup(rg);
        setRgName(rg.name);
        setRgDesc(rg.description);
        setRgContacts([...rg.contacts]);
        setRcName('');
        setRcPhone('');
        setRcEmail('');
        setRcSchedule('');
        setIsEditRuleGroupOpen(true);
    };
    const handleUpdateRuleGroup = () => {
        if (!rgName.trim() || rgContacts.length === 0) {
            toast.error('Nome e contactos obrigatórios.');
            return;
        }
        setRuleGroups(p => p.map(r => r.id === selectedRuleGroup.id ? {...r, name: rgName.trim(), description: rgDesc.trim(), contacts: rgContacts} : r));
        setIsEditRuleGroupOpen(false);
        toast.success('Grupo atualizado!');
    };
    const handleDeleteRuleGroup = (rgId) => {
        setVehicles(p => p.map(v => {
            const nextIds = getVehicleRuleGroupIds(v).filter(id => String(id) !== String(rgId));
            return {
                ...v,
                contactRuleGroupIds: nextIds,
                contactRuleGroupId: nextIds[0] ?? null,
            };
        }));
        setRuleGroups(p => p.filter(r => r.id !== rgId));
        toast.success('Grupo removido.');
    };
    // ASSIGN RULE GROUP TO VEHICLE
    const handleOpenAssignRule = (v) => {
        if (!requireResourcePermission(canUpdateResources)) return;
        setSelectedVehicle(v);
        setAssignRuleGroupIds(getVehicleRuleGroupIds(v));
        setIsAssignRuleOpen(true);
    };
    const toggleAssignRuleGroup = (ruleGroupId) => {
        const id = String(ruleGroupId);
        setAssignRuleGroupIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };
    const handleAssignRule = async () => {
        if (!requireResourcePermission(canUpdateResources)) return;
        if (!selectedVehicle) return;
        const rgIds = assignRuleGroupIds.map(toApiId).filter(id => id !== null);
        setSubmittingResource(true);
        try {
            const response = await api.post(
                resourceEndpoints.assignContactRuleGroups,
                resourceContactRuleGroupsPayload(selectedVehicle, assignRuleGroupIds)
            );
            const updated = normalizeResource(response.data?.data ?? response.data?.resource ?? response.data, {
                ...selectedVehicle,
                contactRuleGroupId: rgIds[0] ?? null,
                contactRuleGroupIds: assignRuleGroupIds,
            }, resourceStatuses);
            setVehicles(p => p.map(v => v.id === selectedVehicle.id ? updated : v));
            setSelectedVehicle(updated);
            setIsAssignRuleOpen(false);
            toast.success(rgIds.length ? `${rgIds.length} regra(s) atribuída(s).` : 'Regras removidas do veiculo.');
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Nao foi possível atualizar a regra do recurso.'));
        } finally {
            setSubmittingResource(false);
        }
    };
    const handleDeleteVehicle = async (vehicle) => {
        if (!requireResourcePermission(canDeleteResources)) return;
        if (!window.confirm(`Eliminar recurso "${vehicle.name}"?`)) return;
        setSubmittingResource(true);
        try {
            await api.post(resourceEndpoints.delete, {
                id: vehicle.id,
                uuid: vehicle.uuid,
            });
            setVehicles(p => p.filter(v => v.id !== vehicle.id));
            if (selectedVehicle?.id === vehicle.id) setSelectedVehicle(null);
            toast.success('Recurso removido.');
        } catch (error) {
            toast.error(getApiErrorMessage(error, 'Nao foi possível remover o recurso.'));
        } finally {
            setSubmittingResource(false);
        }
    };
    // TAGS
    const handleViewTagHistory = (t) => {
        setSelectedTag(t);
        setIsTagHistoryOpen(true);
    };
    const getTagIncidents = (id) => tagIncidents[String(id)] || [];
    const handleDeactivateTag = (t) => {
        setSelectedTag(t);
        setDeactivateReason('');
        setDeactivateNotes('');
        setIsDeactivateTagOpen(true);
    };
    const confirmDeactivateTag = () => {
        if (!deactivateReason) {
            toast.error('Selecione um motivo');
            return;
        }
        setDeactivatedTags(p => [{id: selectedTag.id, deactivatedDate: new Date().toISOString().split('T')[0], reason: deactivateReason, notes: deactivateNotes, vehicle: '-'}, ...p]);
        setAvailableTags(p => p.filter(t => t.id !== selectedTag.id));
        setIsDeactivateTagOpen(false);
        toast.success(`Tag ${selectedTag.id} dada como baixa`);
    };

    // Stats
    const totalVehicles = vehicles.length;
    const activeVehicles = vehicles.filter(v => v.status === 'active').length;
    const vehiclesWithDriver = vehicles.filter(v => hasVehicleDrivers(v)).length;
    const totalTagsInVehicles = vehicles.filter(v => v.tag !== null).length;
    const fleetManagers = fleetManagerOptions.map(manager => {
        const managedVehicles = vehicles.filter(vehicle => String(vehicle.fleetManagerId) === String(manager.id));
        return {
            ...manager,
            category: manager.category || 'Sem categoria',
            stats: {
                vehicles: managedVehicles.length || manager.stats?.vehicles || 0,
                incidents: {total: manager.stats?.incidents?.total || 0},
                sms: {sent: manager.stats?.sms?.sent || 0},
                calls: {total: manager.stats?.calls?.total || 0},
            },
        };
    });

    // Rule group status badge for a vehicle
    const RuleGroupBadge = ({vehicle}) => {
        const assignedGroups = getVehicleRuleGroups(vehicle);
        if (!assignedGroups.length) return <span className="text-xs text-slate-400 italic">Sem regra</span>;
        const active = !hasVehicleDrivers(vehicle);
        return (
            <div className="flex flex-wrap gap-1.5">
                {assignedGroups.map(rg => (
                    <span key={rg.id} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-2 h-2 rounded-full ${active ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}/>
                        {rg.name} {active ? '(Ativa)' : '(Suspensa)'}
                    </span>
                ))}
            </div>
        );
    };

    const DriverSummary = ({vehicle, avatarClassName = 'bg-emerald-600', emptyText = 'Sem condutor'}) => {
        const drivers = getVehicleDrivers(vehicle);
        const primary = drivers[0];

        if (!primary) return <span className="text-xs text-amber-600 italic">{emptyText}</span>;

        return (
            <div className="flex items-center gap-2 min-w-0">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${avatarClassName}`}>
                    <span className="text-white text-[10px] font-bold">{primary.name.split(' ').map(n => n[0]).join('')}</span>
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{primary.name}{drivers.length > 1 ? ` +${drivers.length - 1}` : ''}</p>
                    <p className="text-xs text-slate-500 truncate">{primary.phone}</p>
                </div>
            </div>
        );
    };

    // CONTACT RULE FORM (shared between create & edit)
    const RuleGroupForm = () => (
        <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Nome do Grupo *</Label><Input value={rgName} onChange={e => setRgName(e.target.value)} placeholder="Ex: Turno Diurno/Noturno"/></div>
                <div className="space-y-2"><Label>Descrição</Label><Input value={rgDesc} onChange={e => setRgDesc(e.target.value)} placeholder="Descrição breve"/></div>
            </div>
            {/* Existing contacts */}
            {rgContacts.length > 0 && (
                <div className="space-y-2">
                    <Label className="text-sm font-semibold">Contactos ({rgContacts.length})</Label>
                    {rgContacts.map(c => (
                        <div key={c.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center"><span className="text-white font-bold text-xs">{c.name.split(' ').map(n => n[0]).join('')}</span></div>
                                <div>
                                    <p className="font-medium text-sm text-slate-900">{c.name}</p>
                                    <div className="flex items-center gap-3 text-xs text-slate-500">
                                        <span className="flex items-center gap-1"><Phone size={10}/>{c.phone}</span>
                                        {c.email && <span className="flex items-center gap-1"><Mail size={10}/>{c.email}</span>}
                                        <span className="flex items-center gap-1"><Clock size={10}/>{c.schedule}</span>
                                    </div>
                                </div>
                            </div>
                            <Button variant="ghost" size="sm" className="text-red-500 h-8 w-8 p-0" onClick={() => handleRemoveRgContact(c.id)}><Trash2 size={14}/></Button>
                        </div>
                    ))}
                </div>
            )}
            {/* Add contact form */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                <Label className="text-sm font-semibold text-blue-900">Adicionar Contacto</Label>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Nome *</Label><Input value={rcName} onChange={e => setRcName(e.target.value)} placeholder="João Silva" className="bg-white"/></div>
                    <div className="space-y-1"><Label className="text-xs">Telefone *</Label><Input value={rcPhone} onChange={e => setRcPhone(e.target.value.replace(/\D/g, '').slice(0, 9))} placeholder="912345678" inputMode="numeric" maxLength={9} className="bg-white"/></div>
                    <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={rcEmail} onChange={e => setRcEmail(e.target.value)} placeholder="email@empresa.pt" className="bg-white"/></div>
                    <div className="space-y-1"><Label className="text-xs">Horário</Label><Input value={rcSchedule} onChange={e => setRcSchedule(e.target.value)} placeholder="08:00 - 18:00 ou 24h" className="bg-white"/></div>
                </div>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleAddRgContact}><Plus size={14} className="mr-1"/>Adicionar</Button>
            </div>
        </div>
    );

    // VEHICLE CARD
    const VehicleCard = ({vehicle, showRule = false}) => {
        const manager = vehicle.fleetManager || getFleetManager(vehicle.fleetManagerId);
        const assignedRuleGroups = getVehicleRuleGroups(vehicle);
        const vehicleStatus = getStatusConfig(vehicle.status);
        return (
            <Card key={vehicle.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-40 bg-slate-100 cursor-pointer" onClick={() => handleViewVehicle(vehicle)}>
                    {vehicle.image ? (
                        <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover"/>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <ImageIcon size={32}/>
                        </div>
                    )}
                    <div className="absolute top-3 right-3"><Badge className={vehicleStatus.color}>{vehicleStatus.label}</Badge></div>
                    <div className="absolute bottom-3 left-3"><Badge variant="outline" className="bg-white/90 backdrop-blur font-secondary font-bold text-lg px-3">{vehicle.reference}</Badge></div>
                    <div className="absolute top-3 left-3 flex gap-2">
                        <PermissionGate permission={PERMISSIONS.RESOURCE_UPDATE}>
                            <Button size="sm" variant="secondary" className="bg-white/90 hover:bg-white h-8 w-8 p-0" onClick={e => {
                                e.stopPropagation();
                                handleEditVehicle(vehicle);
                            }}><Edit size={14}/></Button>
                        </PermissionGate>
                        <PermissionGate permission={PERMISSIONS.RESOURCE_DELETE}>
                            <Button size="sm" variant="secondary" className="bg-white/90 hover:bg-white h-8 w-8 p-0 text-red-600" disabled={submittingResource} onClick={e => {
                                e.stopPropagation();
                                handleDeleteVehicle(vehicle);
                            }}><Trash2 size={14}/></Button>
                        </PermissionGate>
                    </div>
                </div>
                <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                        <div><h3 className="font-secondary font-bold text-slate-900">{vehicle.name}</h3>
                            <p className="text-sm text-slate-600">{vehicle.resourceType || '-'} • {vehicle.color}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{vehicle.category}</Badge>
                    </div>
                    {manager && <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg border border-blue-100"><Shield className="text-blue-600" size={14}/><span className="text-xs text-blue-800"><strong>Gestor:</strong> {manager.name}</span></div>}
                    <div className={`p-3 rounded-lg mb-3 cursor-pointer transition-colors ${hasVehicleDrivers(vehicle) ? 'bg-emerald-50 border border-emerald-200 hover:bg-emerald-100' : 'bg-amber-50 border border-amber-200 hover:bg-amber-100'}`}
                         onClick={() => handleOpenAssignDriver(vehicle)}>
                        {hasVehicleDrivers(vehicle) ? (
                            <div className="flex items-center gap-3">
                                <DriverSummary vehicle={vehicle} avatarClassName="bg-emerald-600"/>
                                <Edit className="text-emerald-600" size={16}/>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center"><User className="text-amber-700" size={20}/></div>
                                <div className="flex-1"><p className="font-medium text-amber-800">Sem Condutor</p><p className="text-xs text-amber-600">Clique para atribuir</p></div>
                                <Plus className="text-amber-600" size={16}/>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2"><QrCode className="text-purple-600" size={16}/><span className="text-sm text-slate-600">{vehicle.tag ? '1 tag' : 'Sem tag'}</span></div>
                        {vehicle.tag && <TagIdDisplay tagId={vehicle.tag.id} size="small"/>}
                    </div>
                    {showRule && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="flex items-center justify-between">
                                <RuleGroupBadge vehicle={vehicle}/>
                                <PermissionGate permission={PERMISSIONS.RESOURCE_UPDATE}>
                                    <Button variant="ghost" size="sm" className="text-blue-600 text-xs h-7 px-2" onClick={() => handleOpenAssignRule(vehicle)}>
                                        {getVehicleRuleGroupIds(vehicle).length ? <><Edit size={12} className="mr-1"/>Alterar</> : <><Link2 size={12} className="mr-1"/>Atribuir Regra</>}
                                    </Button>
                                </PermissionGate>
                            </div>
                            {assignedRuleGroups.length > 0 && !hasVehicleDrivers(vehicle) && (
                                <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                                    {assignedRuleGroups.map(rg => (
                                        <div key={rg.id} className="mb-2 last:mb-0">
                                            <p className="text-xs text-green-800 font-medium mb-1">{rg.name}: {rg.contacts.length} contacto(s) ativo(s)</p>
                                            {rg.contacts.map(c => <p key={`${rg.id}-${c.id}`} className="text-xs text-green-700">• {c.name} — {c.schedule}</p>)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div><h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Tags Online</h1><p className="text-slate-600 font-primary mt-1">Gestão de veículos, condutores e tags</p></div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => navigate('/contact-rules')} data-testid="header-contact-rules-btn"><Bell size={18} className="mr-2"/>Regras de Contacto</Button>
                        <PermissionGate permission={PERMISSIONS.RESOURCE_UPDATE}>
                            <Button variant="outline" onClick={handleOpenSwapDriver} disabled={resourcesLoading || submittingResource}><ArrowLeftRight size={18} className="mr-2"/>Trocar Condutor</Button>
                        </PermissionGate>
                        <PermissionGate permission={PERMISSIONS.RESOURCE_CREATE}>
                            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleOpenCreateVehicle} disabled={objectTypesLoading || submittingResource} data-testid="new-vehicle-btn"><Plus size={18} className="mr-2"/>Novo Veículo/Equipamento</Button>
                        </PermissionGate>
                    </div>
                </div>

                {(resourcesError || objectTypesError) && (
                    <div className="space-y-3">
                        {resourcesError && (
                            <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700">{resourcesError}</Card>
                        )}
                        {objectTypesError && (
                            <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">{objectTypesError}</Card>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <Card className="p-5">
                        <div className="flex items-center justify-between mb-2"><p className="text-slate-600 text-sm font-medium">Total Veículos</p><Car className="text-blue-600" size={20}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{totalVehicles}</p><p className="text-xs text-slate-500 mt-1">{activeVehicles} ativos</p></Card>
                    <Card className="p-5">
                        <div className="flex items-center justify-between mb-2"><p className="text-slate-600 text-sm font-medium">Com Condutor</p><User className="text-emerald-600" size={20}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{vehiclesWithDriver}</p><p className="text-xs text-slate-500 mt-1">{totalVehicles - vehiclesWithDriver} sem condutor</p></Card>
                    <Card className="p-5">
                        <div className="flex items-center justify-between mb-2"><p className="text-slate-600 text-sm font-medium">Tags em Veículos</p><QrCode className="text-purple-600" size={20}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{totalTagsInVehicles}</p></Card>
                    <Card className="p-5">
                        <div className="flex items-center justify-between mb-2"><p className="text-slate-600 text-sm font-medium">Tags Disponíveis</p><Package className="text-amber-600" size={20}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{availableTags.length}</p></Card>
                    <Card className="p-5">
                        <div className="flex items-center justify-between mb-2"><p className="text-slate-600 text-sm font-medium">Regras de Contacto</p><Bell className="text-blue-600" size={20}/></div>
                        <p className="text-2xl font-secondary font-bold text-slate-900">{ruleGroups.length}</p><p className="text-xs text-slate-500 mt-1">Grupos criados</p></Card>
                </div>

                <Tabs defaultValue="vehicles" className="w-full">
                    <TabsList className="flex-wrap h-auto gap-1">
                        <TabsTrigger value="vehicles" className="flex items-center gap-2"><Car size={16}/>Veículos ({totalVehicles})</TabsTrigger>
                        <TabsTrigger value="no-driver" className="flex items-center gap-2"><UserCog size={16}/>Sem Condutor ({vehiclesWithoutDriver.length})</TabsTrigger>
                        <TabsTrigger value="managers" className="flex items-center gap-2"><BarChart3 size={16}/>Gestores ({fleetManagers.length})</TabsTrigger>
                        <TabsTrigger value="available-tags" className="flex items-center gap-2"><Package size={16}/>Tags Disponíveis ({availableTags.length})</TabsTrigger>
                        <TabsTrigger value="deactivated-tags" className="flex items-center gap-2"><Ban size={16}/>Tags em Baixa ({deactivatedTags.length})</TabsTrigger>
                    </TabsList>

                    {/* VEHICLES TAB */}
                    <TabsContent value="vehicles" className="mt-6">
                        <div className="flex flex-wrap gap-3 mb-6 items-center">
                            <div className="flex-1 min-w-[220px] relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16}/><Input placeholder="Pesquisar..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/></div>
                            <Select value={filterCategory} onValueChange={setFilterCategory}><SelectTrigger className="w-[180px]"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="all">Todas Categorias</SelectItem><SelectItem value="Frota Elétrica">Frota Elétrica</SelectItem><SelectItem value="Escritório">Escritório</SelectItem><SelectItem
                                value="Fábrica 1">Fábrica 1</SelectItem><SelectItem value="Fábrica 2">Fábrica 2</SelectItem></SelectContent></Select>
                            <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-[150px]"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem>{resourceStatusOptions.map(option => (
                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                            ))}</SelectContent></Select>
                            {/* View Mode Toggle */}
                            <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="view-mode-toggle">
                                <button
                                    type="button"
                                    data-testid="view-mode-grid"
                                    onClick={() => setViewMode('grid')}
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                                >
                                    <LayoutGrid size={16}/> Grelha
                                </button>
                                <button
                                    type="button"
                                    data-testid="view-mode-list"
                                    onClick={() => setViewMode('list')}
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors border-l border-slate-200 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                                >
                                    <List size={16}/> Lista
                                </button>
                            </div>
                        </div>

                        {viewMode === 'grid' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vehicles-grid">
                                {resourcesLoading ? (
                                    <Card className="col-span-full p-8 text-center text-slate-500">
                                        <Car className="mx-auto mb-3 text-slate-300" size={48}/>
                                        <p>A carregar recursos...</p>
                                    </Card>
                                ) : filteredVehicles.length > 0 ? (
                                    filteredVehicles.map(v => <VehicleCard key={v.id} vehicle={v} showRule={true}/>)
                                ) : (
                                    <Card className="col-span-full p-8 text-center text-slate-500">
                                        <Car className="mx-auto mb-3 text-slate-300" size={48}/>
                                        <p>Nenhum recurso encontrado</p>
                                    </Card>
                                )}
                            </div>
                        ) : (
                            <Card data-testid="vehicles-list">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-slate-50">
                                            <TableHead className="w-[120px]">Referência</TableHead>
                                            <TableHead>Marca / Modelo</TableHead>
                                            <TableHead className="w-[90px]">Tipo</TableHead>
                                            <TableHead>Categoria</TableHead>
                                            <TableHead>Condutor</TableHead>
                                            <TableHead>Tag</TableHead>
                                            <TableHead>Regra de Contacto</TableHead>
                                            <TableHead className="w-[110px]">Estado</TableHead>
                                            <TableHead className="w-[170px] text-right">Ações</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {!resourcesLoading && filteredVehicles.map(v => {
                                            const assignedRuleGroups = getVehicleRuleGroups(v);
                                            const ruleActive = assignedRuleGroups.length > 0 && !hasVehicleDrivers(v);
                                            const vehicleStatus = getStatusConfig(v.status);
                                            return (
                                                <TableRow key={v.id} className="hover:bg-slate-50" data-testid={`vehicle-row-${v.id}`}>
                                                    <TableCell>
                                                        <Badge variant="outline" className="font-mono font-bold text-sm px-2 py-1">{v.reference}</Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-3">
                                                            {v.image ? (
                                                                <img src={v.image} alt={v.name} className="w-12 h-9 object-cover rounded border border-slate-200"/>
                                                            ) : (
                                                                <div className="w-12 h-9 flex items-center justify-center rounded border border-slate-200 bg-slate-100 text-slate-400">
                                                                    <ImageIcon size={16}/>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <p className="font-medium text-slate-900">{v.name}</p>
                                                                <p className="text-xs text-slate-500">{v.color}</p>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-sm text-slate-600">{v.resourceType || '-'}</TableCell>
                                                    <TableCell><Badge variant="outline" className="text-xs">{v.category}</Badge></TableCell>
                                                    <TableCell>
                                                        <DriverSummary vehicle={v}/>
                                                    </TableCell>
                                                    <TableCell>
                                                        {v.tag ? <TagIdDisplay tagId={v.tag.id} size="small"/> : <span className="text-xs text-slate-400 italic">Sem tag</span>}
                                                    </TableCell>
                                                    <TableCell>
                                                        {assignedRuleGroups.length > 0 ? (
                                                            <div className="flex flex-wrap gap-1">
                                                                {assignedRuleGroups.map(rg => (
                                                                    <span key={rg.id} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${ruleActive ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
                                                                        <span className={`w-1.5 h-1.5 rounded-full ${ruleActive ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}/>
                                                                        {rg.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-slate-400 italic">Sem regra</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell><Badge className={vehicleStatus.color}>{vehicleStatus.label}</Badge></TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center justify-end gap-1">
                                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleViewVehicle(v)} title="Ver detalhes">
                                                                <Eye size={14} className="text-slate-600"/>
                                                            </Button>
                                                            <PermissionGate permission={PERMISSIONS.RESOURCE_UPDATE}>
                                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditVehicle(v)} title="Editar">
                                                                    <Edit size={14} className="text-slate-600"/>
                                                                </Button>
                                                            </PermissionGate>
                                                            <PermissionGate permission={PERMISSIONS.RESOURCE_UPDATE}>
                                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleOpenAssignRule(v)} title="Atribuir regra">
                                                                    <Bell size={14} className="text-blue-600"/>
                                                                </Button>
                                                            </PermissionGate>
                                                            <PermissionGate permission={PERMISSIONS.RESOURCE_DELETE}>
                                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" disabled={submittingResource} onClick={() => handleDeleteVehicle(v)} title="Eliminar">
                                                                    <Trash2 size={14} className="text-red-600"/>
                                                                </Button>
                                                            </PermissionGate>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                {resourcesLoading ? (
                                    <div className="p-8 text-center text-slate-500">
                                        <Car className="mx-auto mb-3 text-slate-300" size={48}/>
                                        <p>A carregar recursos...</p>
                                    </div>
                                ) : filteredVehicles.length === 0 && (
                                    <div className="p-8 text-center text-slate-500">
                                        <Car className="mx-auto mb-3 text-slate-300" size={48}/>
                                        <p>Nenhum recurso encontrado</p>
                                    </div>
                                )}
                            </Card>
                        )}
                    </TabsContent>

                    {/* SEM CONDUTOR TAB */}
                    <TabsContent value="no-driver" className="mt-6">
                        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
                            <AlertTriangle className="text-amber-600" size={20}/>
                            <div><p className="font-medium text-amber-900">Veículos sem condutor atribuído</p><p className="text-sm text-amber-700">A regra de contacto está <strong>ativa</strong> nestes veículos. Quando atribuir um condutor, a regra fica automaticamente suspensa.</p></div>
                        </div>
                        {vehiclesWithoutDriver.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{vehiclesWithoutDriver.map(v => <VehicleCard key={v.id} vehicle={v} showRule={true}/>)}</div>
                        ) : <div className="text-center py-12 text-slate-500"><CheckCircle2 className="mx-auto mb-3 text-emerald-300" size={48}/><p>Todos os veículos têm condutor</p></div>}
                    </TabsContent>

                    {/* MANAGERS TAB */}
                    <TabsContent value="managers" className="mt-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {fleetManagers.map(m => (
                                <Card key={m.id} className="overflow-hidden">
                                    <div className="p-4 bg-slate-50 border-b">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center"><span className="text-white font-bold text-lg">{m.name.split(' ').map(n => n[0]).join('')}</span></div>
                                            <div className="flex-1"><h3 className="font-secondary font-bold text-lg">{m.name}</h3>
                                                <div className="flex items-center gap-2"><Badge variant="outline" className="text-xs">{m.category}</Badge><span className="text-xs text-slate-600">{m.stats.vehicles} veículos</span></div>
                                            </div>
                                            <Shield className="text-blue-600" size={24}/></div>
                                    </div>
                                    <div className="p-4">
                                        <div className="grid grid-cols-3 gap-3">
                                            <div className="text-center p-3 bg-blue-50 rounded-lg"><p className="text-xs text-blue-800 mb-1">Ocorrências</p><p className="text-2xl font-bold text-blue-900">{m.stats.incidents.total}</p></div>
                                            <div className="text-center p-3 bg-emerald-50 rounded-lg"><p className="text-xs text-emerald-800 mb-1">SMS</p><p className="text-2xl font-bold text-emerald-900">{m.stats.sms.sent}</p></div>
                                            <div className="text-center p-3 bg-purple-50 rounded-lg"><p className="text-xs text-purple-800 mb-1">Chamadas</p><p className="text-2xl font-bold text-purple-900">{m.stats.calls.total}</p></div>
                                        </div>
                                        <div className="mt-3 pt-3 border-t text-sm text-slate-600 flex items-center gap-4"><span className="flex items-center gap-1"><Mail size={14}/>{m.email}</span><span className="flex items-center gap-1"><Phone size={14}/>{m.phone}</span></div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>

                    {/* AVAILABLE/DEACTIVATED TAGS */}
                    <TabsContent value="available-tags" className="mt-6"><Card>
                        <div className="p-4 border-b"><h2 className="font-secondary font-bold">Tags Disponíveis</h2></div>
                        <Table><TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Data</TableHead><TableHead>Categoria</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader><TableBody>{availableTags.map(t => <TableRow key={t.id}><TableCell><TagIdDisplay tagId={t.id} size="large"/></TableCell><TableCell
                            className="text-slate-600">{t.assignedDate}</TableCell><TableCell><Badge variant="outline">{t.category}</Badge></TableCell><TableCell><Badge className="bg-emerald-100 text-emerald-700">Disponível</Badge></TableCell><TableCell><Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50"
                                                                                                                                                                                                                                                                      onClick={() => handleDeactivateTag(t)}><Ban size={14} className="mr-1"/>Dar
                            Baixa</Button></TableCell></TableRow>)}</TableBody></Table>{availableTags.length === 0 && <div className="p-8 text-center text-slate-500"><Package className="mx-auto mb-3 text-slate-300" size={48}/><p>Sem tags</p></div>}</Card></TabsContent>
                    <TabsContent value="deactivated-tags" className="mt-6"><Card>
                        <div className="p-4 border-b"><h2 className="font-secondary font-bold">Tags em Baixa</h2></div>
                        {deactivatedTagsError && (
                            <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                {deactivatedTagsError}
                            </div>
                        )}
                        <Table><TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Data</TableHead><TableHead>Motivo</TableHead><TableHead>Veículo</TableHead><TableHead>Notas</TableHead></TableRow></TableHeader><TableBody>{deactivatedTags.map(t => {
                            const I = tagReasonConfig[t.reason]?.icon || Ban;
                            return (<TableRow key={t.id}><TableCell>
                                <div className="opacity-60"><TagIdDisplay tagId={t.id}/></div>
                            </TableCell><TableCell className="text-slate-600">{t.deactivatedDate}</TableCell><TableCell><Badge className={tagReasonConfig[t.reason]?.color || 'bg-slate-100'}><I size={12} className="mr-1"/>{tagReasonConfig[t.reason]?.label || t.reason}</Badge></TableCell><TableCell><Badge
                                variant="outline">{t.vehicle}</Badge></TableCell><TableCell className="text-sm text-slate-600 max-w-[200px] truncate">{t.notes || '-'}</TableCell></TableRow>);
                        })}</TableBody></Table>{!deactivatedTagsError && deactivatedTags.length === 0 && <div className="p-8 text-center text-slate-500"><Ban className="mx-auto mb-3 text-slate-300" size={48}/><p>Sem tags em baixa</p></div>}</Card></TabsContent>
                </Tabs>

                {/* ===== DIALOGS ===== */}

                {/* Vehicle Detail */}
                <Dialog open={isVehicleDetailOpen} onOpenChange={setIsVehicleDetailOpen}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="flex items-center gap-2"><Car className="text-blue-600" size={20}/>Detalhes do Veículo</DialogTitle></DialogHeader>
                        {selectedVehicle && (<div className="space-y-6">
                            <div className="flex gap-6">
                                <div className="w-48 h-32 rounded-lg overflow-hidden bg-slate-100">
                                    {selectedVehicle.image ? (
                                        <img src={selectedVehicle.image} alt={selectedVehicle.name} className="w-full h-full object-cover"/>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                                            <ImageIcon size={32}/>
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2"><h3 className="font-secondary font-bold text-xl">{selectedVehicle.name}</h3>{(() => {
                                        const vehicleStatus = getStatusConfig(selectedVehicle.status);
                                        return <Badge className={vehicleStatus.color}>{vehicleStatus.label}</Badge>;
                                    })()}</div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div><span className="text-slate-500">Referência:</span><span className="ml-2 font-bold">{selectedVehicle.reference}</span></div>
                                        <div><span className="text-slate-500">Tipo:</span><span className="ml-2">{selectedVehicle.resourceType || '-'}</span></div>
                                        <div><span className="text-slate-500">Categoria:</span><span className="ml-2">{selectedVehicle.category}</span></div>
                                    </div>
                                </div>
                            </div>
                            {(() => {
                                const vehicleStatus = getStatusConfig(selectedVehicle.status);
                                const vehicleType = getVehicleObjectType(selectedVehicle);
                                const TIcon = OBJECT_TYPE_ICONS[vehicleType?.icon] || Package;
                                const manager = selectedVehicle.fleetManager || getFleetManager(selectedVehicle.fleetManagerId);
                                const resourceStatus = getResourceStatus(selectedVehicle.resourceStatusId);
                                const driverRule = getDriverRule(selectedVehicle.driverContactRuleId);
                                const dynamicFields = vehicleType?.fields || [];
                                const detailFieldValues = getVehicleDetailFieldValues(selectedVehicle);

                                return (
                                    <div className="space-y-4">
                                        {vehicleType && (
                                            <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                                                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: `${vehicleType.color}15`}}>
                                                    <TIcon size={20} style={{color: vehicleType.color}}/>
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-secondary font-bold text-sm text-slate-900">{vehicleType.name}</p>
                                                    {vehicleType.description && <p className="text-xs text-slate-500 break-words">{vehicleType.description}</p>}
                                                </div>
                                            </div>
                                        )}

                                        <div className="p-4 rounded-lg border border-slate-200 bg-white">
                                            <h4 className="font-secondary font-bold mb-3 flex items-center gap-2"><FileText size={16}/>Informacao do Recurso</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <DetailField label="Referencia" value={selectedVehicle.reference} valueClassName="font-bold"/>
                                                <DetailField label="Nome" value={selectedVehicle.resourceName || selectedVehicle.name}/>
                                                <DetailField label="Tipo" value={selectedVehicle.resourceType}/>
                                                <DetailField label="Estado do recurso" value={resourceStatus?.name || vehicleStatus.label}/>
                                                <DetailField label="Categoria" value={selectedVehicle.category}/>
                                                <DetailField label="Gestor" value={manager?.name || ''}/>
                                                <DetailField label="Descricao" value={selectedVehicle.description} className="md:col-span-2"/>
                                                <DetailField label="Notas" value={selectedVehicle.notes} className="md:col-span-2"/>
                                                {driverRule && <DetailField label="Regra de contacto ao condutor" value={driverRule.name} className="md:col-span-2"/>}
                                            </div>
                                        </div>

                                        {dynamicFields.length > 0 && (
                                            <div className="p-4 rounded-lg border border-slate-200 bg-white">
                                                <h4 className="font-secondary font-bold mb-3 flex items-center gap-2"><Package size={16}/>Campos do Tipo</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {dynamicFields.map(field => {
                                                        const value = getPathValue(detailFieldValues, field.valuePath);
                                                        const colSpan = field.type === 'textarea' || field.type === 'coords' ? 'md:col-span-2' : '';
                                                        return (
                                                            <div key={field.id} className={`min-w-0 ${colSpan}`}>
                                                                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{field.label}</p>
                                                                <p className="mt-1 text-sm text-slate-900 break-words">{formatDetailValue(value, field)}</p>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                            <div className={`p-4 rounded-lg ${hasVehicleDrivers(selectedVehicle) ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                                <h4 className="font-secondary font-bold mb-3 flex items-center gap-2"><User size={16}/>Condutor</h4>
                                {hasVehicleDrivers(selectedVehicle) ? <div className="space-y-3">
                                        {getVehicleDrivers(selectedVehicle).map(driver => (
                                            <div key={driver.id} className="flex items-center gap-4">
                                                <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center"><span className="text-white font-bold text-lg">{driver.name.split(' ').map(n => n[0]).join('')}</span></div>
                                                <div className="flex-1"><p className="font-medium text-lg">{driver.name}</p>
                                                    <div className="flex gap-4 mt-1 text-sm text-slate-600"><span className="flex items-center gap-1"><Mail size={14}/>{driver.email}</span><span className="flex items-center gap-1"><Phone size={14}/>{driver.phone}</span></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    : <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-14 h-14 rounded-full bg-amber-200 flex items-center justify-center"><User className="text-amber-700" size={24}/></div>
                                            <div><p className="font-medium text-amber-800">Sem Condutor</p></div>
                                        </div>
                                        <PermissionGate permission={PERMISSIONS.RESOURCE_UPDATE}>
                                            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => {
                                                setIsVehicleDetailOpen(false);
                                                handleOpenAssignDriver(selectedVehicle);
                                            }}><Plus size={14} className="mr-1"/>Atribuir</Button>
                                        </PermissionGate></div>}
                            </div>
                            {/* Rule Group */}
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between mb-2"><h4 className="font-secondary font-bold flex items-center gap-2"><Bell size={16}/>Regra de Contacto</h4><RuleGroupBadge vehicle={selectedVehicle}/></div>
                                {(() => {
                                    const assignedRuleGroups = getVehicleRuleGroups(selectedVehicle);
                                    return assignedRuleGroups.length > 0 ? (
                                        <div className="space-y-3">
                                            {assignedRuleGroups.map(rg => (
                                                <div key={rg.id}>
                                                    <p className="text-sm font-medium text-slate-800">{rg.name}</p>
                                                    {rg.description && <p className="text-sm text-slate-700 mb-2">{rg.description}</p>}
                                                    {rg.contacts.map(c => <p key={`${rg.id}-${c.id}`} className="text-sm text-slate-600">• {c.name} — {c.phone} ({c.schedule})</p>)}
                                                </div>
                                            ))}
                                        </div>
                                    ) : <p className="text-sm text-slate-500">Nenhuma regra atribuída</p>;
                                })()}
                            </div>
                            {selectedVehicle.tag && <div className="p-4 bg-purple-50 rounded-lg border border-purple-200"><h4 className="font-secondary font-bold mb-3 flex items-center gap-2"><QrCode size={16}/>Tag</h4>
                                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                                    <div className="flex items-center gap-4"><TagIdDisplay tagId={selectedVehicle.tag.id} size="large"/><p className="text-sm text-slate-600 flex items-center gap-1"><MapPin size={12}/>{selectedVehicle.tag.location}</p></div>
                                    <Button variant="outline" size="sm" onClick={() => handleViewTagHistory(selectedVehicle.tag)}><History size={14} className="mr-1"/>Histórico ({tagIncidentsLoading ? '...' : getTagIncidents(selectedVehicle.tag.id).length})</Button></div>
                            </div>}
                        </div>)}
                    </DialogContent>
                </Dialog>

                {/* Create/Edit Vehicle */}
                {/* ==================== Novo Veículo/Equipamento ==================== */}
                <Dialog open={canCreateResources && isCreateVehicleOpen} onOpenChange={(o) => {
                    if (!o) {
                        setIsCreateVehicleOpen(false);
                    }
                }}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Plus className="text-blue-600" size={20}/>
                                Novo Veículo/Equipamento
                            </DialogTitle>
                            <DialogDescription>
                                {createStep === 1
                                    ? 'Passo 1 de 2 — Selecione o tipo de objeto que vai registar'
                                    : `Passo 2 de 2 — Preencha os dados do ${getObjectType(createTypeId)?.name || 'objeto'}`}
                            </DialogDescription>
                        </DialogHeader>

                        {createStep === 1 && (
                            <div className="py-2 space-y-4" data-testid="create-step-1">
                                <p className="text-xs text-slate-500">
                                    Os tipos de objeto e seus campos são configurados em <strong>Configurações → Objetos</strong>.
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {objectTypesLoading && (
                                        <p className="col-span-full text-center text-sm text-slate-500 py-4">A carregar tipos de objeto...</p>
                                    )}
                                    {!objectTypesLoading && objectTypes.length === 0 && (
                                        <p className="col-span-full text-center text-sm text-slate-500 py-4">
                                            {objectTypesError || 'Sem tipos de objeto disponíveis.'}
                                        </p>
                                    )}
                                    {!objectTypesLoading && objectTypes.map(t => {
                                        const TIcon = OBJECT_TYPE_ICONS[t.icon] || Package;
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => handlePickType(t.id)}
                                                data-testid={`pick-type-${t.id}`}
                                                className="flex flex-col items-start gap-2 p-4 rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-md transition-all text-left bg-white"
                                            >
                                                <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{backgroundColor: `${t.color}15`}}>
                                                    <TIcon size={22} style={{color: t.color}}/>
                                                </div>
                                                <div>
                                                    <p className="font-secondary font-bold text-sm text-slate-900">{t.name}</p>
                                                    <p className="text-xs text-slate-500 line-clamp-2">{t.description}</p>
                                                </div>
                                                <Badge variant="outline" className="text-[10px]">{t.fields.length} campos</Badge>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {createStep === 2 && createTypeId && (
                            <div className="py-2 space-y-4" data-testid="create-step-2">
                                <div className="space-y-2">
                                    <Label>Referência <span className="text-red-500">*</span></Label>
                                    <Input
                                        value={newReference}
                                        onChange={e => setNewReference(e.target.value)}
                                        placeholder="Ex: RES-001"
                                        data-testid="new-reference"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nome <span className="text-red-500">*</span></Label>
                                        <Input value={newResourceName} onChange={e => setNewResourceName(e.target.value)} data-testid="new-resource-name"/>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Tipo <span className="text-red-500">*</span></Label>
                                        <Input value={newResourceType} onChange={e => setNewResourceType(e.target.value)} data-testid="new-resource-type"/>
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <Label>Descrição</Label>
                                        <Textarea rows={2} value={newDescription} onChange={e => setNewDescription(e.target.value)} data-testid="new-resource-description"/>
                                    </div>
                                    <div className="space-y-2 col-span-2">
                                        <Label>Notas</Label>
                                        <Textarea rows={2} value={newNotes} onChange={e => setNewNotes(e.target.value)} data-testid="new-resource-notes"/>
                                    </div>
                                </div>

                                {(() => {
                                    const type = getObjectType(createTypeId);
                                    if (!type) return null;
                                    const TIcon = OBJECT_TYPE_ICONS[type.icon] || Package;
                                    return (
                                        <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: `${type.color}15`}}>
                                                <TIcon size={20} style={{color: type.color}}/>
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-secondary font-bold text-sm text-slate-900">{type.name}</p>
                                                <p className="text-xs text-slate-500">{type.description}</p>
                                            </div>
                                            <Button variant="ghost" size="sm" onClick={() => setCreateStep(1)}>Mudar tipo</Button>
                                        </div>
                                    );
                                })()}

                                {/* Foto do veículo/equipamento (pedido c) */}
                                {(() => {
                                    const type = getObjectType(createTypeId);
                                    const previewUrl = newImage || type?.defaultImage || null;
                                    return (
                                        <div className="flex items-start gap-3 p-3 rounded-lg border border-dashed border-slate-300 bg-white">
                                            <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0 flex items-center justify-center">
                                                {previewUrl ? (
                                                    <img src={previewUrl} alt="Pré-visualização" className="w-full h-full object-cover" data-testid="vehicle-image-preview"/>
                                                ) : (
                                                    <ImageIcon size={22} className="text-slate-300"/>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-900">Foto do veículo/equipamento</p>
                                                <p className="text-xs text-slate-500 mt-0.5">
                                                    {newImage
                                                        ? 'Imagem personalizada selecionada.'
                                                        : type?.defaultImage
                                                            ? `A usar a imagem padrão do tipo "${type.name}".`
                                                            : 'Sem imagem definida. Pode escolher uma da biblioteca ou carregar a sua.'}
                                                </p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <Button type="button" variant="outline" size="sm" onClick={() => setIsImagePickerOpen(true)} data-testid="open-image-picker-btn">
                                                        <ImageIcon size={14} className="mr-1"/>
                                                        {previewUrl ? 'Alterar imagem' : 'Escolher imagem'}
                                                    </Button>
                                                    {newImage && (
                                                        <Button type="button" variant="ghost" size="sm" onClick={() => setNewImage(null)} data-testid="clear-image-btn">
                                                            <X size={14} className="mr-1"/> Usar padrão
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                <div className="grid grid-cols-2 gap-4">
                                    {(getObjectType(createTypeId)?.fields || []).map(f => {
                                        const val = getPathValue(createFieldValues, f.valuePath);
                                        const update = (v) => setCreateFieldValues(p => setPathValue(p, f.valuePath, v));
                                        const key = `dyn-field-${f.attributeKey || f.id}`;
                                        const colSpan = f.type === 'textarea' || f.type === 'coords' ? 'col-span-2' : '';
                                        return (
                                            <div key={f.id} className={`space-y-2 ${colSpan}`}>
                                                <Label className="text-sm">
                                                    {f.label} {f.required && <span className="text-red-500">*</span>}
                                                </Label>
                                                {f.type === 'text' && (
                                                    <Input value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                )}
                                                {f.type === 'number' && (
                                                    <Input type="number" value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                )}
                                                {f.type === 'date' && (
                                                    <Input type="date" value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                )}
                                                {f.type === 'time' && (
                                                    <Input type="time" value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                )}
                                                {f.type === 'datetime-local' && (
                                                    <Input type="datetime-local" value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                )}
                                                {['email', 'tel', 'url'].includes(f.type) && (
                                                    <Input type={f.type} value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                )}
                                                {f.type === 'textarea' && (
                                                    <Textarea rows={2} value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                )}
                                                {f.type === 'select' && (
                                                    <Select value={val || ''} onValueChange={update}>
                                                        <SelectTrigger data-testid={key}><SelectValue placeholder="Selecionar..."/></SelectTrigger>
                                                        <SelectContent>
                                                            {(f.options || []).map(opt => (
                                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                                {f.type === 'coords' && (
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <Input type="number" step="0.000001" placeholder="Latitude (ex: 38.7169)"
                                                               value={val?.lat || ''} onChange={e => update({...val, lat: e.target.value})}/>
                                                        <Input type="number" step="0.000001" placeholder="Longitude (ex: -9.1399)"
                                                               value={val?.lng || ''} onChange={e => update({...val, lng: e.target.value})}/>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm">Estado do recurso</Label>
                                    <Select
                                        value={newResourceStatusId || 'none'}
                                        onValueChange={value => setNewResourceStatusId(value === 'none' ? '' : value)}
                                        disabled={resourceStatusesLoading || activeResourceStatuses.length === 0}
                                    >
                                        <SelectTrigger data-testid="new-resource-status-id">
                                            <SelectValue placeholder="Selecionar"/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sem estado</SelectItem>
                                            {activeResourceStatuses.map(resourceStatus => (
                                                <SelectItem key={resourceStatus.id} value={String(resourceStatus.id)}>
                                                    {resourceStatus.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {resourceStatusesLoading && <p className="text-xs text-slate-500">A carregar estados...</p>}
                                    {!resourceStatusesLoading && activeResourceStatuses.length === 0 && (
                                        <p className="text-xs text-slate-500">{resourceStatusesError || 'Sem estados ativos disponíveis.'}</p>
                                    )}
                                </div>

                                {/* Metadados comuns (categoria + gestor + regra de contacto ao condutor) */}
                                <div className="space-y-4 pt-3 border-t border-slate-200">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm">Categoria <span className="text-red-500">*</span></Label>
                                            <Select value={newCategory} onValueChange={handleNewCategoryChange} disabled={departmentsLoading || departments.length === 0}>
                                                <SelectTrigger><SelectValue placeholder="Selecionar"/></SelectTrigger>
                                                <SelectContent>
                                                    {departments.map(department => (
                                                        <SelectItem key={department.id} value={department.id.toString()}>{department.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {departmentsLoading && <p className="text-xs text-slate-500">A carregar departamentos...</p>}
                                            {!departmentsLoading && departments.length === 0 && (
                                                <p className="text-xs text-slate-500">{departmentsError || 'Sem departamentos disponíveis.'}</p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm">Gestor</Label>
                                            <Select value={newFleetManagerId || 'none'} onValueChange={value => setNewFleetManagerId(value === 'none' ? '' : value)} disabled={!newCategory || fleetManagersLoading}>
                                                <SelectTrigger><SelectValue placeholder="Selecionar"/></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">Sem gestor</SelectItem>
                                                    {newFleetManagerOptions.map(m => <SelectItem key={m.id} value={m.id.toString()}>{m.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                            {fleetManagersLoading && <p className="text-xs text-slate-500">A carregar utilizadores...</p>}
                                            {!fleetManagersLoading && newCategory && newFleetManagerOptions.length === 0 && (
                                                <p className="text-xs text-slate-500">{fleetManagersError || 'Sem utilizadores disponíveis.'}</p>
                                            )}
                                        </div>
                                    </div>
                                    {/* Regra com condutor */}
                                    <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <Label className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                                            <Bell size={14}/>Regra de contacto ao condutor
                                        </Label>
                                        <Select value={newDriverRuleChoice} onValueChange={setNewDriverRuleChoice} disabled={driverRulesLoading}>
                                            <SelectTrigger data-testid="driver-rule-select" className="bg-white"><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="inherit">
                                                    <span className="text-slate-700">Herdar (categoria → geral)</span>
                                                </SelectItem>
                                                {driverRuleOptions.filter(r => r.scope !== 'general').map(r => (
                                                    <SelectItem key={r.id} value={String(r.id)}>
                                                        {r.name} <span className="text-slate-400">({r.category})</span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {driverRulesLoading && <p className="text-xs text-blue-700">A carregar regras...</p>}
                                        {!driverRulesLoading && driverRuleOptions.filter(r => r.scope !== 'general').length === 0 && (
                                            <p className="text-xs text-blue-700">{driverRulesError || 'Sem regras especificas disponíveis.'}</p>
                                        )}
                                        <p className="text-xs text-blue-700">Deixe em "Herdar" para usar a regra da categoria (ou a Geral se não houver).</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="outline" disabled={submittingResource} onClick={() => setIsCreateVehicleOpen(false)}>Cancelar</Button>
                            {createStep === 2 && (
                                <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateVehicle} disabled={submittingResource} data-testid="confirm-create-btn">
                                    <Plus size={16} className="mr-2"/>{submittingResource ? 'A guardar...' : 'Criar'}
                                </Button>
                            )}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <Dialog open={canUpdateResources && isEditVehicleOpen} onOpenChange={setIsEditVehicleOpen}><DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle><Edit className="text-blue-600 inline mr-2" size={20}/>Editar Veículo</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Referência <span className="text-red-500">*</span></Label>
                            <Input value={editReference} onChange={e => setEditReference(e.target.value)} data-testid="edit-reference"/>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Nome <span className="text-red-500">*</span></Label>
                                <Input value={editResourceName} onChange={e => setEditResourceName(e.target.value)} data-testid="edit-resource-name"/>
                            </div>
                            <div className="space-y-2">
                                <Label>Tipo <span className="text-red-500">*</span></Label>
                                <Input value={editResourceType} onChange={e => setEditResourceType(e.target.value)} data-testid="edit-resource-type"/>
                            </div>
                            <div className="space-y-2 col-span-2">
                                <Label>Descrição</Label>
                                <Textarea rows={2} value={editDescription} onChange={e => setEditDescription(e.target.value)} data-testid="edit-resource-description"/>
                            </div>
                            <div className="space-y-2 col-span-2">
                                <Label>Notas</Label>
                                <Textarea rows={2} value={editNotes} onChange={e => setEditNotes(e.target.value)} data-testid="edit-resource-notes"/>
                            </div>
                        </div>
                        {(() => {
                            const type = getObjectType(selectedVehicle?.typeId);
                            if (!type) {
                                return (
                                    <div className="text-sm text-slate-500 italic rounded-lg border border-dashed border-slate-200 p-4">
                                        Sem campos dinamicos para este tipo de recurso.
                                    </div>
                                );
                            }
                            const TIcon = OBJECT_TYPE_ICONS[type.icon] || Package;
                            return (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: `${type.color}15`}}>
                                            <TIcon size={20} style={{color: type.color}}/>
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-secondary font-bold text-sm text-slate-900">{type.name}</p>
                                            <p className="text-xs text-slate-500">{type.description}</p>
                                        </div>
                                    </div>
                                    {(() => {
                                        const previewUrl = editImage || type.defaultImage || null;
                                        const usingDefault = !editImage || editImage === type.defaultImage;
                                        return (
                                            <div className="flex items-start gap-3 p-3 rounded-lg border border-dashed border-slate-300 bg-white">
                                                <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0 flex items-center justify-center">
                                                    {previewUrl ? (
                                                        <img src={previewUrl} alt="Pre-visualizacao" className="w-full h-full object-cover" data-testid="edit-vehicle-image-preview"/>
                                                    ) : (
                                                        <ImageIcon size={22} className="text-slate-300"/>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-900">Foto do veiculo/equipamento</p>
                                                    <p className="text-xs text-slate-500 mt-0.5">
                                                        {editImage && !usingDefault
                                                            ? 'Imagem personalizada selecionada.'
                                                            : type.defaultImage
                                                                ? `A usar a imagem padrao do tipo "${type.name}".`
                                                                : 'Sem imagem definida. Pode escolher uma da biblioteca ou carregar a sua.'}
                                                    </p>
                                                    <div className="flex items-center gap-2 mt-2">
                                                        <Button type="button" variant="outline" size="sm" onClick={() => setIsEditImagePickerOpen(true)} data-testid="open-edit-image-picker-btn">
                                                            <ImageIcon size={14} className="mr-1"/>
                                                            {previewUrl ? 'Alterar imagem' : 'Escolher imagem'}
                                                        </Button>
                                                        {editImage && editImage !== type.defaultImage && (
                                                            <Button type="button" variant="ghost" size="sm" onClick={() => setEditImage(null)} data-testid="clear-edit-image-btn">
                                                                <X size={14} className="mr-1"/> Usar padrao
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    <div className="grid grid-cols-2 gap-4">
                                        {(type.fields || []).map(f => {
                                            const val = getPathValue(editFieldValues, f.valuePath);
                                            const update = (v) => setEditFieldValues(p => setPathValue(p, f.valuePath, v));
                                            const key = `edit-dyn-field-${f.attributeKey || f.id}`;
                                            const colSpan = f.type === 'textarea' || f.type === 'coords' ? 'col-span-2' : '';
                                            return (
                                                <div key={f.id} className={`space-y-2 ${colSpan}`}>
                                                    <Label className="text-sm">
                                                        {f.label} {f.required && <span className="text-red-500">*</span>}
                                                    </Label>
                                                    {f.type === 'text' && (
                                                        <Input value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                    )}
                                                    {f.type === 'number' && (
                                                        <Input type="number" value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                    )}
                                                    {f.type === 'date' && (
                                                        <Input type="date" value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                    )}
                                                    {f.type === 'time' && (
                                                        <Input type="time" value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                    )}
                                                    {f.type === 'datetime-local' && (
                                                        <Input type="datetime-local" value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                    )}
                                                    {['email', 'tel', 'url'].includes(f.type) && (
                                                        <Input type={f.type} value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                    )}
                                                    {f.type === 'textarea' && (
                                                        <Textarea rows={2} value={val || ''} onChange={e => update(e.target.value)} data-testid={key}/>
                                                    )}
                                                    {f.type === 'select' && (
                                                        <Select value={val || ''} onValueChange={update}>
                                                            <SelectTrigger data-testid={key}><SelectValue placeholder="Selecionar..."/></SelectTrigger>
                                                            <SelectContent>
                                                                {(f.options || []).map(opt => (
                                                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                    {f.type === 'coords' && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <Input type="number" step="0.000001" placeholder="Latitude (ex: 38.7169)"
                                                                   value={val?.lat || ''} onChange={e => update({...val, lat: e.target.value})}/>
                                                            <Input type="number" step="0.000001" placeholder="Longitude (ex: -9.1399)"
                                                                   value={val?.lng || ''} onChange={e => update({...val, lng: e.target.value})}/>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                        <div className="space-y-2">
                            <Label>Estado do recurso</Label>
                            <Select
                                value={editResourceStatusId || 'none'}
                                onValueChange={value => setEditResourceStatusId(value === 'none' ? '' : value)}
                                disabled={resourceStatusesLoading || resourceStatuses.length === 0}
                            >
                                <SelectTrigger data-testid="edit-resource-status-id">
                                    <SelectValue placeholder="Selecionar"/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sem estado</SelectItem>
                                    {resourceStatuses.map(resourceStatus => (
                                        <SelectItem key={resourceStatus.id} value={String(resourceStatus.id)}>
                                            {resourceStatus.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {resourceStatusesLoading && <p className="text-xs text-slate-500">A carregar estados...</p>}
                            {!resourceStatusesLoading && resourceStatuses.length === 0 && (
                                <p className="text-xs text-slate-500">{resourceStatusesError || 'Sem estados disponíveis.'}</p>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Categoria <span className="text-red-500">*</span></Label>
                                <Select value={editCategory} onValueChange={handleEditCategoryChange} disabled={departmentsLoading || departments.length === 0}>
                                    <SelectTrigger><SelectValue placeholder="Selecionar"/></SelectTrigger>
                                    <SelectContent>
                                        {departments.map(department => (
                                            <SelectItem key={department.id} value={department.id.toString()}>{department.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {departmentsLoading && <p className="text-xs text-slate-500">A carregar departamentos...</p>}
                                {!departmentsLoading && departments.length === 0 && (
                                    <p className="text-xs text-slate-500">{departmentsError || 'Sem departamentos disponíveis.'}</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label>Gestor</Label>
                                <Select value={editFleetManagerId || 'none'} onValueChange={value => setEditFleetManagerId(value === 'none' ? '' : value)} disabled={!editCategory || fleetManagersLoading}>
                                    <SelectTrigger><SelectValue placeholder="Selecionar"/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sem gestor</SelectItem>
                                        {editFleetManagerOptions.map(m => <SelectItem key={m.id} value={m.id.toString()}>{m.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {fleetManagersLoading && <p className="text-xs text-slate-500">A carregar utilizadores...</p>}
                                {!fleetManagersLoading && editCategory && editFleetManagerOptions.length === 0 && (
                                    <p className="text-xs text-slate-500">{fleetManagersError || 'Sem utilizadores disponíveis.'}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setIsEditVehicleOpen(false)}>Cancelar</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveVehicle} disabled={submittingResource}>Guardar</Button></DialogFooter></DialogContent></Dialog>

                {/* Assign Driver */}
                <Dialog open={canUpdateResources && isAssignDriverOpen} onOpenChange={(open) => {
                    setIsAssignDriverOpen(open);
                    if (!open) setIsDriverPickerOpen(false);
                }}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle><UserCog className="text-emerald-600 inline mr-2" size={20}/>Atribuir Condutor</DialogTitle>
                            <DialogDescription>Veículo {selectedVehicle?.reference}</DialogDescription>
                        </DialogHeader>
                    <div className="space-y-4 py-4">
                        {hasVehicleDrivers(selectedVehicle) && (
                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs text-blue-600">Condutores Atuais</p>
                                    <Button variant="outline" size="sm" className="text-red-600 border-red-200" disabled={submittingResource} onClick={() => setSelectedDriverIds([])}>Remover todos</Button>
                                </div>
                                <div className="space-y-2">
                                    {getVehicleDrivers(selectedVehicle).map(driver => (
                                        <DriverSummary key={driver.id} vehicle={{drivers: [driver]}} avatarClassName="bg-blue-600"/>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label>Condutor</Label>
                            <Popover open={isDriverPickerOpen} onOpenChange={setIsDriverPickerOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={isDriverPickerOpen}
                                        disabled={fleetManagersLoading || submittingResource}
                                        className="w-full justify-between font-normal"
                                    >
                                        <span className={`truncate ${selectedDriver ? 'text-slate-900' : 'text-slate-500'}`}>
                                            {selectedDriverLabel}
                                        </span>
                                        <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50"/>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                    <Command>
                                        <CommandInput placeholder="Pesquisar condutor..."/>
                                        <CommandList>
                                            <CommandEmpty>Nenhum condutor encontrado.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandItem
                                                    value="Sem condutor"
                                                    onSelect={() => {
                                                        handleSelectedDriverChange('none');
                                                        setIsDriverPickerOpen(false);
                                                    }}
                                                >
                                                    <Check className={`mr-2 h-4 w-4 ${selectedDriverId === 'none' ? 'opacity-100' : 'opacity-0'}`}/>
                                                    Sem condutor
                                                </CommandItem>
                                                {driverOptions.map(driver => {
                                                    const driverValue = driver.id.toString();
                                                    const driverLabel = formatDriverOption(driver);
                                                    return (
                                                        <CommandItem
                                                            key={driver.id}
                                                            value={`${driverLabel} ${driver.email || ''} ${driver.id}`}
                                                            onSelect={() => {
                                                                handleSelectedDriverChange(driverValue);
                                                                setIsDriverPickerOpen(false);
                                                            }}
                                                        >
                                                            <Check className={`mr-2 h-4 w-4 ${selectedDriverId === driverValue ? 'opacity-100' : 'opacity-0'}`}/>
                                                            <span className="truncate">{driverLabel}</span>
                                                        </CommandItem>
                                                    );
                                                })}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            {fleetManagersLoading && <p className="text-xs text-slate-500">A carregar utilizadores...</p>}
                            {!fleetManagersLoading && driverOptions.length === 0 && <p className="text-xs text-slate-500">{fleetManagersError || 'Sem utilizadores disponíveis.'}</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" disabled={submittingResource} onClick={() => setIsAssignDriverOpen(false)}>Cancelar</Button>
                        <Button disabled={submittingResource} className={selectedDriverIds.length === 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                            onClick={handleAssignDriver}>{submittingResource ? 'A guardar...' : selectedDriverIds.length === 0 ? 'Remover' : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
                </Dialog>

                {/* Swap Drivers */}
                <Dialog open={canUpdateResources && isSwapDriverOpen} onOpenChange={setIsSwapDriverOpen}><DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle><ArrowLeftRight className="text-blue-600 inline mr-2" size={20}/>Trocar Condutores</DialogTitle><DialogDescription>Clique em dois veículos para
                    trocar</DialogDescription></DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="border rounded-lg overflow-hidden"><Table><TableHeader><TableRow className="bg-slate-50"><TableHead
                            className="w-10">Sel.</TableHead><TableHead>ID</TableHead><TableHead>Marca/Modelo</TableHead><TableHead>Condutor</TableHead><TableHead>Telefone</TableHead><TableHead>Email</TableHead></TableRow></TableHeader><TableBody>{vehicles.map(v => {
                            const resourceId = v.id.toString();
                            const isA = swapVehicleA === resourceId;
                            const isB = swapVehicleB === resourceId;
                            return (<TableRow key={v.id} className={`cursor-pointer ${isA ? 'bg-blue-50 border-l-4 border-l-blue-500' : isB ? 'bg-green-50 border-l-4 border-l-green-500' : 'hover:bg-slate-50'}`} onClick={() => {
                                if (isA) setSwapVehicleA(''); else if (isB) setSwapVehicleB(''); else if (!swapVehicleA) setSwapVehicleA(resourceId); else if (!swapVehicleB) setSwapVehicleB(resourceId); else {
                                    setSwapVehicleA(resourceId);
                                    setSwapVehicleB('');
                                }
                            }}><TableCell>{isA && <Badge className="bg-blue-500 text-white text-xs">A</Badge>}{isB && <Badge className="bg-green-500 text-white text-xs">B</Badge>}{!isA && !isB && <div className="w-6 h-6 border-2 border-slate-300 rounded"/>}</TableCell><TableCell className="font-mono font-bold">{v.reference}</TableCell><TableCell><span
                                className="font-medium">{v.name}</span><span className="text-xs text-slate-500 ml-2">{v.resourceType || '-'}</span></TableCell><TableCell><DriverSummary vehicle={v}/></TableCell><TableCell
                                className="text-sm text-slate-600">{getVehicleDrivers(v)[0]?.phone || '-'}</TableCell><TableCell className="text-sm text-slate-600">{getVehicleDrivers(v)[0]?.email || '-'}</TableCell></TableRow>);
                        })}</TableBody></Table></div>
                        {swapVehicleA && swapVehicleB && (() => {
                            const a = vehicles.find(v => String(v.id) === String(swapVehicleA));
                            const b = vehicles.find(v => String(v.id) === String(swapVehicleB));
                            return a && b ? (<div className="p-4 bg-blue-50 rounded-lg border border-blue-200"><p className="text-xs text-blue-600 font-medium mb-3 text-center">Resultado da Troca</p>
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 text-center p-3 bg-white rounded-lg"><p className="font-bold text-sm">{a.reference}</p><p className="text-xs mt-1">{getVehicleDrivers(b).length ? <span className="text-emerald-700">{getVehicleDrivers(b).map(driver => driver.name).join(', ')}</span> : <span className="text-amber-600">Sem condutor</span>}</p></div>
                                    <ArrowLeftRight className="text-blue-500" size={24}/>
                                    <div className="flex-1 text-center p-3 bg-white rounded-lg"><p className="font-bold text-sm">{b.reference}</p><p className="text-xs mt-1">{getVehicleDrivers(a).length ? <span className="text-emerald-700">{getVehicleDrivers(a).map(driver => driver.name).join(', ')}</span> : <span className="text-amber-600">Sem condutor</span>}</p></div>
                                </div>
                            </div>) : null;
                        })()}</div>
                    <DialogFooter><Button variant="outline" disabled={submittingResource} onClick={() => setIsSwapDriverOpen(false)}>Cancelar</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSwapDrivers} disabled={!swapVehicleA || !swapVehicleB || submittingResource}><ArrowLeftRight size={16}
                                                                                                                                                                                                                                                                                                                    className="mr-2"/>{submittingResource ? 'A guardar...' : 'Trocar'}
                    </Button></DialogFooter></DialogContent></Dialog>

                {/* Create Rule Group */}
                <Dialog open={isCreateRuleGroupOpen} onOpenChange={setIsCreateRuleGroupOpen}><DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle><Plus className="text-blue-600 inline mr-2" size={20}/>Novo Grupo de Regras de Contacto</DialogTitle><DialogDescription>Crie um grupo reutilizável de contactos para veículos
                    sem condutor</DialogDescription></DialogHeader><RuleGroupForm/><DialogFooter><Button variant="outline" onClick={() => setIsCreateRuleGroupOpen(false)}>Cancelar</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveRuleGroup}><Plus size={16} className="mr-2"/>Criar
                    Grupo</Button></DialogFooter></DialogContent></Dialog>

                {/* Edit Rule Group */}
                <Dialog open={isEditRuleGroupOpen} onOpenChange={setIsEditRuleGroupOpen}><DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle><Edit className="text-blue-600 inline mr-2" size={20}/>Editar Grupo de Regras</DialogTitle><DialogDescription>Atualizar contactos do
                    grupo {selectedRuleGroup?.name}</DialogDescription></DialogHeader><RuleGroupForm/><DialogFooter><Button variant="outline" onClick={() => setIsEditRuleGroupOpen(false)}>Cancelar</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={handleUpdateRuleGroup}>Guardar</Button></DialogFooter></DialogContent></Dialog>

                {/* Assign Rule Group to Vehicle */}
                <Dialog open={canUpdateResources && isAssignRuleOpen} onOpenChange={setIsAssignRuleOpen}><DialogContent><DialogHeader><DialogTitle><Link2 className="text-blue-600 inline mr-2" size={20}/>Atribuir Regra de Contacto</DialogTitle><DialogDescription>Veículo {selectedVehicle?.reference} — {selectedVehicle?.name}</DialogDescription></DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Regra Tags sem responsavel</Label>
                            <div className="space-y-2 rounded-lg border border-slate-200 p-3 max-h-72 overflow-y-auto">
                                {!ruleGroupsLoading && ruleGroups.map(rg => {
                                    const checked = assignRuleGroupIds.includes(String(rg.id));
                                    return (
                                        <label key={rg.id} className="flex items-start gap-3 rounded-md p-2 hover:bg-slate-50 cursor-pointer">
                                            <Checkbox checked={checked} onCheckedChange={() => toggleAssignRuleGroup(rg.id)} disabled={submittingResource}/>
                                            <span className="min-w-0">
                                                <span className="block text-sm font-medium text-slate-900">{rg.name}</span>
                                                <span className="block text-xs text-slate-500">{rg.contacts.length} contacto(s)</span>
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                            {ruleGroupsLoading && <p className="text-xs text-slate-500">A carregar regras tags sem responsavel...</p>}
                            {!ruleGroupsLoading && ruleGroups.length === 0 && (
                                <p className="text-xs text-slate-500">{ruleGroupsError || 'Sem regras tags sem responsável disponíveis.'}</p>
                            )}
                        </div>
                        {assignRuleGroupIds.length > 0 && (
                            <div className="space-y-2">
                                {getRuleGroups(assignRuleGroupIds).map(rg => (
                                    <div key={rg.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <p className="text-xs font-semibold text-blue-900 mb-2">{rg.name}</p>
                                        {rg.contacts.map(c => <p key={`${rg.id}-${c.id}`} className="text-xs text-blue-700">• {c.name} — {c.phone} ({c.schedule})</p>)}
                                    </div>
                                ))}
                                <p className="text-xs text-blue-600 italic">{hasVehicleDrivers(selectedVehicle) ? 'As regras ficarão suspensas ate o condutor ser removido.' : 'As regras ficarão imediatamente ativas.'}</p>
                            </div>
                        )}</div>
                    <DialogFooter><Button variant="outline" onClick={() => setIsAssignRuleOpen(false)}>Cancelar</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={handleAssignRule} disabled={submittingResource}>{assignRuleGroupIds.length ? 'Guardar Regras' : 'Remover Regras'}</Button></DialogFooter></DialogContent></Dialog>

                {/* Tag History */}
                <Dialog open={isTagHistoryOpen} onOpenChange={setIsTagHistoryOpen}><DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto"><DialogHeader><DialogTitle><History className="text-purple-600 inline mr-2" size={20}/>Histórico</DialogTitle>{selectedTag &&
                    <div className="flex items-center gap-3 mt-2"><TagIdDisplay tagId={selectedTag.id} size="xlarge"/></div>}</DialogHeader>{selectedTag && <div className="space-y-4 py-4">{(() => {
                    const incs = getTagIncidents(selectedTag.id);
                    if (tagIncidentsLoading) return <div className="text-center py-8 text-slate-500"><Clock className="mx-auto mb-3 text-slate-300" size={48}/><p>A carregar ocorrências...</p></div>;
                    if (tagIncidentsError) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{tagIncidentsError}</div>;
                    return incs.length > 0 ? <div className="space-y-3">{incs.map(i => <div key={i.id} className="p-4 bg-slate-50 rounded-lg border">
                        <div className="flex justify-between mb-2"><span className="font-medium">{i.type}</span><Badge className={incidentStatusConfig[i.status]?.color || 'bg-slate-100 text-slate-600'}>{incidentStatusConfig[i.status]?.label || i.status}</Badge></div>
                        <p className="text-sm text-slate-600 mb-2">{i.description}</p>
                        <div className="flex gap-4 text-xs text-slate-500"><span className="flex items-center gap-1"><Calendar size={12}/>{i.date}</span><span className="flex items-center gap-1"><User size={12}/>{i.reportedBy}</span></div>
                    </div>)}</div> : <div className="text-center py-8 text-slate-500"><CheckCircle2 className="mx-auto mb-3 text-emerald-300" size={48}/><p>Sem ocorrências</p></div>;
                })()}</div>}</DialogContent></Dialog>

                {/* Deactivate Tag */}
                <Dialog open={isDeactivateTagOpen} onOpenChange={setIsDeactivateTagOpen}><DialogContent><DialogHeader><DialogTitle className="text-red-600"><Ban className="inline mr-2" size={20}/>Dar Baixa</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">{selectedTag && <div className="p-4 bg-red-50 rounded-lg border border-red-200 text-center"><TagIdDisplay tagId={selectedTag.id} size="xlarge"/></div>}
                        <div className="space-y-2"><Label>Motivo *</Label><Select value={deactivateReason} onValueChange={setDeactivateReason}><SelectTrigger><SelectValue placeholder="Selecionar"/></SelectTrigger><SelectContent><SelectItem value="lost">Perdida</SelectItem><SelectItem value="damaged">Danificada</SelectItem><SelectItem
                            value="other">Outro</SelectItem></SelectContent></Select></div>
                        <div className="space-y-2"><Label>Notas</Label><Textarea value={deactivateNotes} onChange={e => setDeactivateNotes(e.target.value)} rows={3}/></div>
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setIsDeactivateTagOpen(false)}>Cancelar</Button><Button className="bg-red-600 hover:bg-red-700" onClick={confirmDeactivateTag}><Ban size={16} className="mr-2"/>Confirmar</Button></DialogFooter></DialogContent></Dialog>

                {/* Image picker for new vehicle (pedido c) */}
                <VehicleImagePicker
                    open={isImagePickerOpen}
                    onClose={() => setIsImagePickerOpen(false)}
                    onConfirm={(url) => setNewImage(url)}
                    currentImage={newImage}
                    objectTypeId={createTypeId}
                    defaultImage={getObjectType(createTypeId)?.defaultImage}
                    objectTypeName={getObjectType(createTypeId)?.name}
                />
                <VehicleImagePicker
                    open={isEditImagePickerOpen}
                    onClose={() => setIsEditImagePickerOpen(false)}
                    onConfirm={(url) => setEditImage(url)}
                    currentImage={editImage}
                    objectTypeId={selectedVehicle?.typeId}
                    defaultImage={getObjectType(selectedVehicle?.typeId)?.defaultImage}
                    objectTypeName={getObjectType(selectedVehicle?.typeId)?.name}
                />
            </div>
        </Layout>
    );
};
